import React, { useState, useRef, useEffect, Suspense, useMemo, useCallback, lazy } from 'react';
import TTSIconButton from './components/TTSIconButton.jsx';
import { HashRouter, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { LazyProfileManager } from './components/LazyComponent';

// Use LazyProfileManager for code splitting (ProfileManager is 40+ KB)
// Newsletter components are now used in NewsletterScreen component, not directly in App.jsx
// FieldTrips is now used in FieldTripsScreen component, not directly in App.jsx
// Lazy import textToSpeech to avoid initialization order issues (textToSpeech -> storage -> logger)
// import textToSpeech from './utils/textToSpeech';
import { useUser, UserProvider } from './contexts/UserContext';
import ErrorBoundary from './components/ErrorBoundary';
import { ErrorRecoveryProvider } from './components/ErrorRecoverySystem';
// Lazy import errorHandling to avoid initialization order issues (errorHandler -> logger)
// import { setupGlobalErrorHandling } from './utils/errorHandling.jsx';
// Lazy import networkRetry to avoid initialization order issues (networkRetry -> errorHandler -> logger)
// import { networkRetry } from './utils/networkRetry';
// Lazy import errorReporting to avoid initialization order issues (errorReporting -> environmentConfig -> logger)
// import { errorReporting } from './utils/errorReporting';
import { storage, STORAGE_KEYS } from './utils/storage';
import { logger } from './utils/logger';
import { TIMEOUTS, RESPONSE_TIMES } from './constants/timing';
import { FEEDBACK_TIMEOUTS } from './constants/feedback';
import {
  SCORE_THRESHOLDS,
  SCORING,
  QUESTION_LIMITS,
  ACHIEVEMENT_THRESHOLDS,
  UNLOCK_THRESHOLDS,
  PROGRESS_THRESHOLDS,
  TIME_THRESHOLDS,
  RATING,
  RECENT_SCORES_MIN_COUNT,
  NETWORK_RETRY,
  REWARD_POINTS
} from './constants/gameplay';
import { SOUND_TYPES, AUDIO_EVENTS, HAPTIC_TYPES } from './constants/audio';
import { APP_MESSAGES, SUBJECT_DISPLAY_NAMES } from './constants/messages';
import AuthCallback from './components/AuthCallback';
import InvitationPage from './components/InvitationPage';
import ParentDashboardWrapper from './components/ParentDashboardWrapper';
// Lazy load components that aren't needed on initial render
// Note: FeedbackOverlay, AchievementBadge, ConfettiEffect are used in GameScreen so kept as static imports
import FeedbackOverlay from './components/FeedbackOverlay';
import AchievementBadge from './components/AchievementBadge';
import ConfettiEffect from './components/ConfettiEffect';
const AudioPreferences = lazy(() => import('./components/AudioPreferences'));
const PerformanceDashboard = lazy(() => import('./components/PerformanceDashboard'));
import LazyImage from './components/LazyImage';
import MobileLoadingState, { GameLoadingState, LoadingOverlay } from './components/MobileLoadingState';
import OfflineManager, { OfflineFallback, useOfflineManager } from './components/OfflineManager';
import OfflineIndicator from './components/OfflineIndicator';
import { useOfflineQueue } from './hooks/useOfflineQueue';
import { ScrollOptimizer } from './utils/mobilePerformanceOptimizer';
import { useAnimations } from './hooks/useAnimations';
import { useAudio } from './hooks/useAudio';
import { useGameState } from './hooks/useGameState';
import { useGameProgress } from './hooks/useGameProgress';
import { useQuestionManager } from './hooks/useQuestionManager';
import { useScreenNavigation } from './hooks/useScreenNavigation';
import { useTheme } from './hooks/useTheme';
import { useStreakSystem } from './hooks/useStreakSystem';
import { useAdaptiveDifficulty } from './hooks/useAdaptiveDifficulty';
import { useDailyChallenge } from './hooks/useDailyChallenge';
import { useSearch } from './hooks/useSearch';
// Lazy load support components
const HelpSystem = lazy(() => import('./components/HelpSystem'));
const ChatSupport = lazy(() => import('./components/ChatSupport'));
import { trackQuestionAnswered, trackLearningProgress } from './utils/learningAnalytics';
// Lazy import analyticsCollector to avoid initialization order issues (analyticsCollector -> logger)
// import { analyticsCollector } from './utils/analytics';
import { triggerHaptic as triggerHapticFeedback, HapticPatterns } from './utils/hapticFeedback';
// Lazy load PWA and notification components
const PWAInstallPrompt = lazy(() => import('./components/PWAInstallPrompt'));
const NotificationSettings = lazy(() => import('./components/NotificationSettings'));
import { PWAInstallButton, PWAStatusIndicator, PWAUpdateNotification } from './components/PWAUtilities';
import SplashScreen from './components/SplashScreen';
import SkipToContent from './components/SkipToContent';
import AccessibleApp from './components/AccessibleApp';
import { AccessibilityProvider } from './components/AccessibilityProvider';
import HomeScreen from './components/screens/HomeScreen';
import GameScreen from './components/screens/GameScreen';
const ImportPage = lazy(() => import('./components/screens/ImportPage'));

// Lazy load heavy screen components for better performance
const CompleteScreen = lazy(() => import('./components/screens/CompleteScreen'));
const AchievementsScreen = lazy(() => import('./components/screens/AchievementsScreen'));
const CustomizeScreen = lazy(() => import('./components/screens/CustomizeScreen'));
const FeedbackScreen = lazy(() => import('./components/screens/FeedbackScreen'));
const ProgressScreen = lazy(() => import('./components/screens/ProgressScreen'));
const NewsletterScreen = lazy(() => import('./components/screens/NewsletterScreen'));
const FieldTripsScreen = lazy(() => import('./components/screens/FieldTripsScreen'));
const ParentReferenceScreen = lazy(() => import('./components/screens/ParentReferenceScreen'));
const ReviewMistakesScreen = lazy(() => import('./components/screens/ReviewMistakesScreen'));
import LegendModal from './components/modals/LegendModal';
const SolutionModal = lazy(() => import('./components/modals/SolutionModal'));
import { generateHint } from './utils/hintUtils';
import { addMistake } from './utils/mistakeTracker';
import { updateConfidenceLevel as updateConfidenceLevelUtil } from './utils/confidenceTracking';
import { clearCanvasByRef } from './utils/canvasUtils';
import { trackGameCompletion, trackGameDropOff, updateDailyChallengeFromGame } from './utils/gameCompletionTracking';
// Lazy import gamification to avoid initialization issues
// import { addCoins, calculateQuestionCoins, getCoins, awardDailyBonus } from './utils/gamification';
// All educationalContent imports loaded dynamically to avoid TDZ errors

// Enhanced questions loaded dynamically to avoid TDZ errors
// Note: phonicsQuestions, mathQuestions, readingQuestions from appQuestionData.js are not used
// App.jsx uses enhancedPhonicQuestions, enhancedMathQuestions, enhancedReadingQuestions directly
// from educationalContent.js instead
import { calendarEvents } from './data/calendarData';
// Lazy load large data files to reduce initial bundle size
// import { spellingWords } from './data/spellingData'; // 306 lines - loaded dynamically
// import { studyGuides } from './data/studyGuidesData'; // 355 lines - loaded dynamically
import { parentReference } from './data/parentReferenceData';
import { baseAchievements, getAchievements, themes, avatars } from './data/appConfigData';
import { useNormalizeGamePath } from './hooks/useNormalizeGamePath';
import { useErrorHandlingInit } from './hooks/useErrorHandlingInit';

// Email sharing utility (extracted from App.jsx)
import { shareModuleCompletion, shareProgressUpdate, getSubjectDisplayName, sendDirectEmail } from './utils/emailSharing';
// Question utilities (extracted from App.jsx)
import {
  shuffleOptions,
  filterSpellingWordsByMonth,
  filterPhonicsQuestionsByDifficulty,
  filterMathQuestionsByDifficulty,
  filterReadingQuestionsByCategory,
  getQuestionCount,
  getCurrentMonth,
  compareAnswers,
  getCorrectAnswer,
  validateAnswer,
  calculatePoints,
  expandQuestionOptions
} from './utils/questionUtils';
import {
  calculateSubjectScore,
  isPerfectScore,
  calculateCorrectAnswers,
  calculatePercentage,
  updateProgressWithCompletion
} from './utils/scoringUtils';
import { checkAchievements as checkAchievementsUtil, checkUnlocks } from './utils/achievementUtils';
import { generateDailyChallenge as generateDailyChallengeUtil, updateLearningStreak as updateLearningStreakUtil } from './utils/learningProgressUtils';
import { submitFeedback as submitFeedbackUtil, calculateFeedbackStats, getDefaultFeedbackValues, getFeedbackSuccessMessage } from './utils/feedbackUtils';
import { socialStudiesQuestions } from './data/socialStudiesData';
import { skipCountingQuestions } from './data/skipCountingData';

function EmmyStudyGame() {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Start with base achievements and empty question arrays - load ALL dynamically to avoid TDZ
  const [achievements, setAchievements] = useState(baseAchievements);
  const [enhancedPhonicQuestions, setEnhancedPhonicQuestions] = useState({ easy: [], medium: [], hard: [] });
  const [enhancedMathQuestions, setEnhancedMathQuestions] = useState({ easy: [], medium: [], hard: [] });
  const [enhancedReadingQuestions, setEnhancedReadingQuestions] = useState([]);
  const [enhancedSpellingWords, setEnhancedSpellingWords] = useState([]);
  const [scienceQuestions, setScienceQuestions] = useState([]);
  const [artQuestions, setArtQuestions] = useState([]);
  const [geographyQuestions, setGeographyQuestions] = useState([]);
  const [historyQuestions, setHistoryQuestions] = useState([]);
  const [interactiveQuestionTypes, setInteractiveQuestionTypes] = useState({});
  
  // Lazy load large data files (661 lines total)
  const [studyGuides, setStudyGuides] = useState({});
  const [spellingWords, setSpellingWords] = useState([]);
  
  // Load ALL educationalContent exports asynchronously after component mount
  // This completely avoids TDZ by not accessing educationalContent during module initialization
  // Split imports to reduce initial bundle size - only load what's needed
  useEffect(() => {
    // Load core educational content (phonics, math, reading, spelling)
    import('./data/educationalContent.js').then(module => {
      try {
        // Load subjectAchievements
        if (module.subjectAchievements && typeof module.subjectAchievements === 'object' && Object.keys(module.subjectAchievements).length) {
          const merged = getAchievements(module.subjectAchievements);
          setAchievements(merged);
        }
        
        // Load enhanced questions
        if (module.enhancedPhonicQuestions) setEnhancedPhonicQuestions(module.enhancedPhonicQuestions);
        if (module.enhancedMathQuestions) setEnhancedMathQuestions(module.enhancedMathQuestions);
        if (module.enhancedReadingQuestions) setEnhancedReadingQuestions(module.enhancedReadingQuestions);
        if (module.enhancedSpellingWords) setEnhancedSpellingWords(module.enhancedSpellingWords);
        if (module.interactiveQuestionTypes) setInteractiveQuestionTypes(module.interactiveQuestionTypes);
      } catch (error) {
        logger.error('Error loading educationalContent:', error);
      }
    }).catch(error => {
      logger.warn('Could not load educationalContent dynamically:', error);
    });
    
    // Lazy load subject-specific content only when needed (reduces initial bundle)
    // Science content (522 lines)
    import('./data/subjects/scienceContent.js').then(module => {
      if (module.scienceQuestions) setScienceQuestions(module.scienceQuestions);
    }).catch(error => logger.warn('Could not load science content:', error));
    
    // Art content (300 lines)
    import('./data/subjects/artContent.js').then(module => {
      if (module.artQuestions) setArtQuestions(module.artQuestions);
    }).catch(error => logger.warn('Could not load art content:', error));
    
    // Geography content (300 lines)
    import('./data/subjects/geographyContent.js').then(module => {
      if (module.geographyQuestions) setGeographyQuestions(module.geographyQuestions);
    }).catch(error => logger.warn('Could not load geography content:', error));
    
    // History content (310 lines)
    import('./data/subjects/historyContent.js').then(module => {
      if (module.historyQuestions) setHistoryQuestions(module.historyQuestions);
    }).catch(error => logger.warn('Could not load history content:', error));
    
    // Lazy load large data files (reduces initial bundle by ~661 lines)
    // Study guides (355 lines)
    import('./data/studyGuidesData.js').then(module => {
      if (module.studyGuides) setStudyGuides(module.studyGuides);
    }).catch(error => logger.warn('Could not load study guides:', error));
    
    // Spelling words (306 lines)
    import('./data/spellingData.js').then(module => {
      if (module.spellingWords) setSpellingWords(module.spellingWords);
    }).catch(error => logger.warn('Could not load spelling data:', error));
  }, []); // Empty deps - only run once on mount
  
  const { user, userProfile, logout, loading, error } = useUser();
  
  // Setup sync test helper for console access
  useEffect(() => {
    if (user && typeof window !== 'undefined') {
      import('./utils/syncTestHelper').then(module => {
        module.setupSyncTest(user);
      });
    }
  }, [user]);
  
  // Add safety check to prevent rendering issues
  const [isInitialized, setIsInitialized] = useState(false);
  
  // Mobile performance optimizations
  const { isOnline, retryWhenOnline } = useOfflineManager();
  const { queueSize } = useOfflineQueue();
  const [scrollOptimizer] = useState(() => new ScrollOptimizer());
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  
  // Animation system
  const animations = useAnimations();
  
  // Audio system
  const {
    playSound,
    playCorrect,
    playIncorrect,
    playComplete,
    playClick,
    playAchievement,
    playCelebration,
    triggerHaptic: audioTriggerHaptic,
    initializeAudio,
    isInitialized: audioInitialized,
    getAudioManager
  } = useAudio();
  
  // Achievement system state
  const [currentAchievement, setCurrentAchievement] = useState(null);
  const [showAchievementBadge, setShowAchievementBadge] = useState(false);
  
  useEffect(() => {
    // Small delay to ensure all context is ready
    const timer = setTimeout(() => {
      setIsInitialized(true);
    }, TIMEOUTS.INIT_DELAY);
    
    return () => clearTimeout(timer);
  }, []);

  // Initialize audio on first user interaction
  useEffect(() => {
    const handleFirstInteraction = () => {
      if (!audioInitialized) {
        initializeAudio();
      }
    };

    // Listen for first user interaction to initialize audio
    const events = [AUDIO_EVENTS.CLICK, AUDIO_EVENTS.TOUCH_START, AUDIO_EVENTS.KEY_DOWN];
    events.forEach(event => {
      document.addEventListener(event, handleFirstInteraction, { once: true });
    });

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleFirstInteraction);
      });
    };
  }, [audioInitialized, initializeAudio]);
  
  // Game state hook
  const {
    score,
    setScore,
    currentQuestion,
    setCurrentQuestion,
    showQuestionSelector,
    setShowQuestionSelector,
    selectedQuestionCount,
    setSelectedQuestionCount,
    customQuestionCount,
    setCustomQuestionCount,
    pendingGame,
    setPendingGame,
    resetGame,
    startGameWithCount: startGameWithCountFromHook
  } = useGameState();

  // Game progress hook
  const {
    progress,
    setProgress,
    saveProgress,
    getQuestionId,
    trackQuestionHistory,
    cleanupQuestionHistory
  } = useGameProgress();
  
  // Debug: Log when progress.avatar changes
  useEffect(() => {
    logger.debug('[App] progress.avatar changed:', progress.avatar);
  }, [progress.avatar]);
  
  // Theme system
  const { theme, toggleTheme, setTheme: setThemePreference, effectiveTheme } = useTheme();
  
  // Streak system
  const { streak, streakMilestone, updateStreak, streakStats } = useStreakSystem({ autoUpdate: true });
  
  // Daily challenge
  const { challenge: dailyChallengeData, progress: challengeProgress, updateProgress: updateChallengeProgress, progressPercentage: challengeProgressPercentage, isCompleted: isChallengeCompleted } = useDailyChallenge();
  
  // Help system
  const [showHelp, setShowHelp] = useState(false);
  
  // Debug: Log when showHelp changes (development only)
  useEffect(() => {
    logger.debug('[App] showHelp state changed:', showHelp);
  }, [showHelp]);
  
  // Adaptive difficulty (will be used per subject)
  const adaptiveDifficultyPhonics = useAdaptiveDifficulty('phonics', { autoAdjust: false });
  const adaptiveDifficultyMath = useAdaptiveDifficulty('math', { autoAdjust: false });
  
  const canvasRefs = [useRef(null), useRef(null), useRef(null), useRef(null), useRef(null)];
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentCanvas, setCurrentCanvas] = useState(null);
  const [showFeedback, setShowFeedback] = useState(null);
  const [answerAnimation, setAnswerAnimation] = useState('');
  const [drawColor, setDrawColor] = useState('#8B5CF6');
  const [showExplanation, setShowExplanation] = useState(false);
  const [correctAnswer, setCorrectAnswer] = useState('');
  const [showAchievement, setShowAchievement] = useState(null);
  const [backgroundMusic, setBackgroundMusic] = useState(null);
  const [isMusicPlaying, setIsMusicPlaying] = useState(false);
  const musicTimeoutRef = useRef(null);
  const activeOscillatorsRef = useRef([]);
  const isMusicPlayingRef = useRef(false);
  const [difficulty, setDifficulty] = useState('medium');
  const [parentQuizMode, setParentQuizMode] = useState(false);
  const [parentQuizWord, setParentQuizWord] = useState(0);
  const [selectedSubject, setSelectedSubject] = useState('phonics');
  const [selectedCategory, setSelectedCategory] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [learningStreak, setLearningStreak] = useState(0);
  // Get current month for default spelling selection (using utility from questionUtils)
  const [selectedSpellingMonth, setSelectedSpellingMonth] = useState(getCurrentMonth());
  const [selectedPhonicsDifficulty, setSelectedPhonicsDifficulty] = useState('medium');
  const [selectedMathDifficulty, setSelectedMathDifficulty] = useState('medium');
  const [selectedReadingCategory, setSelectedReadingCategory] = useState('all');
  const [selectedCalendarMonth, setSelectedCalendarMonth] = useState(new Date().getMonth());
  const [calendarView, setCalendarView] = useState('list'); // 'month', 'week', 'day', or 'list'
  const [selectedWeekStart, setSelectedWeekStart] = useState(() => {
    // Get start of current week (Sunday)
    const today = new Date();
    const day = today.getDay();
    const diff = today.getDate() - day;
    const weekStart = new Date(today);
    weekStart.setDate(diff);
    return weekStart;
  });
  const [selectedDay, setSelectedDay] = useState(new Date());
  const [eventFilters, setEventFilters] = useState({
    showTests: true,
    showEvents: true,
    showHolidays: true,
    showDressCode: true
  });
  const [lastLearningDate, setLastLearningDate] = useState(null);
  const [dailyChallenge, setDailyChallenge] = useState(null);
  const [confidenceLevels, setConfidenceLevels] = useState({});
  const [adaptiveDifficulty, setAdaptiveDifficulty] = useState({});
  const [feedback, setFeedback] = useState([]);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackCategory, setFeedbackCategory] = useState('general');
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [feedbackRating, setFeedbackRating] = useState(5);
  const [showWrongAnswerModal, setShowWrongAnswerModal] = useState(false);
  const [wrongAnswerData, setWrongAnswerData] = useState(null);
  const [showLegendModal, setShowLegendModal] = useState(false);
  const [parentMode, setParentMode] = useState(false);
  const [showScrollToTop, setShowScrollToTop] = useState(false);
  const [selectedNewsletter, setSelectedNewsletter] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareEmail, setShareEmail] = useState('');
  const [showPerformanceDashboard, setShowPerformanceDashboard] = useState(false);
  const [shareMessage, setShareMessage] = useState('');
  // Question selector state is now managed by useGameState hook
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [hintUsed, setHintUsed] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const [showAudioPreferences, setShowAudioPreferences] = useState(false);
  const [showProfileManager, setShowProfileManager] = useState(false);
  
  // Debug: Log when showProfileManager changes (development only)
  useEffect(() => {
    logger.debug('[App] showProfileManager state changed:', showProfileManager);
    logger.debug('[App] Current renderScreen will be called, showProfileManager =', showProfileManager);
  }, [showProfileManager]);
  
  // Expose setShowProfileManager globally for debugging (development only)
  useEffect(() => {
    if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
      window.__setShowProfileManager = setShowProfileManager;
      window.__getShowProfileManager = () => showProfileManager;
      logger.debug('[App] Debug: window.__setShowProfileManager available. Try: window.__setShowProfileManager(true)');
    }
  }, [showProfileManager]);
  // Progress state is now managed by useGameProgress hook

  // Enhanced haptic feedback - maps old types to new patterns
  const triggerHaptic = (type = 'light') => {
    // Map old HAPTIC_TYPES to new HapticPatterns
    const hapticMap = {
      'light': HapticPatterns.BUTTON_CLICK,
      'success': HapticPatterns.SUCCESS,
      'error': HapticPatterns.ERROR,
      'correct': HapticPatterns.CORRECT,
      'incorrect': HapticPatterns.INCORRECT,
      'complete': HapticPatterns.COMPLETE,
      'achievement': HapticPatterns.ACHIEVEMENT
    };
    
    const pattern = hapticMap[type] || HapticPatterns.BUTTON_CLICK;
    triggerHapticFeedback(pattern, 1.0); // Use enhanced haptic with intensity 1.0
    // Also call audio haptic for backward compatibility
    audioTriggerHaptic(type);
  };

  // Progress and question management functions are now provided by hooks

  // Share results functionality
  // shareResults function removed - was unused

  // Direct email sending wrapper (manages state, delegates to utility)
  const handleDirectEmail = async (emailData) => {
    setIsSendingEmail(true);
    setEmailSent(false);
    
    try {
      const result = await sendDirectEmail({ toEmail: shareEmail, subject: emailData.subject, body: emailData.body });
      
      if (result.status === 200) {
        setEmailSent(true);
        setTimeout(() => {
          setShowShareModal(false);
          setShareEmail('');
          setShareMessage('');
          setEmailSent(false);
        }, TIMEOUTS.MODAL_CLOSE);
      }
    } catch (error) {
      logger.error('Email sending failed:', error);
      alert(error.message || APP_MESSAGES.EMAIL_SEND_FAILED);
    } finally {
      setIsSendingEmail(false);
    }
  };

  const handleProgressEmailShare = async (service = 'mailto') => {
    if (service === 'direct') {
      setIsSendingEmail(true);
      setEmailSent(false);
    }

    try {
      const result = await shareProgressUpdate({
        service,
        toEmail: shareEmail,
        progress,
        completedSubjects,
        totalSubjects,
        accuracy,
        questionsAnswered: progress.stats?.totalQuestionsAnswered || 0,
        achievementsCount: progress.achievements.length,
        learningStreak,
        favoriteSubject,
        shareMessage
      });

      if (result.success) {
        if (service === 'direct') {
          setEmailSent(true);
          setTimeout(() => {
    setShowShareModal(false);
    setShareEmail('');
    setShareMessage('');
            setEmailSent(false);
          }, TIMEOUTS.MODAL_CLOSE);
        } else {
          setShowShareModal(false);
          setShareEmail('');
          setShareMessage('');
        }
      } else if (result.error) {
        alert(result.error);
      }
    } catch (error) {
      logger.error('Email sharing failed:', error);
      alert(error.message || APP_MESSAGES.EMAIL_SHARE_FAILED);
    } finally {
      if (service === 'direct') {
        setIsSendingEmail(false);
      }
    }
  };

  const handleEmailShare = async (service = 'mailto') => {
    const subjectName = getSubjectDisplayName(currentScreen);
    const questionsAnswered = currentQuestion + 1; // currentQuestion is 0-indexed, so add 1
    const percentage = calculatePercentage(score, questionsAnswered);

    if (service === 'direct') {
      setIsSendingEmail(true);
      setEmailSent(false);
    }

    try {
      const result = await shareModuleCompletion({
        service,
        toEmail: shareEmail,
        subjectName,
        score,
        questionsAnswered,
        percentage: calculatePercentage(score, questionsAnswered),
        shareMessage
      });

      if (result.success) {
        if (service === 'direct') {
          setEmailSent(true);
          setTimeout(() => {
    setShowShareModal(false);
    setShareEmail('');
    setShareMessage('');
            setEmailSent(false);
          }, TIMEOUTS.MODAL_CLOSE);
        } else {
          setShowShareModal(false);
          setShareEmail('');
          setShareMessage('');
        }
      } else if (result.error) {
        alert(result.error);
      }
    } catch (error) {
      logger.error('Email sharing failed:', error);
      alert(error.message || APP_MESSAGES.EMAIL_MODULE_FAILED);
    } finally {
      if (service === 'direct') {
        setIsSendingEmail(false);
      }
    }
  };

  // Screen navigation hook
  const {
    currentScreen,
    setCurrentScreen,
    breadcrumbs,
    navigateTo,
    updateBreadcrumbs
  } = useScreenNavigation({
    scrollOptimizer,
    playSound,
    triggerHaptic,
    setSelectedNewsletter,
    setSelectedSubject,
    setSelectedCategory,
    setShowSearch,
    setSearchQuery,
    setIsLoading
  });

  // Listen for navigation events from ChatSupport
  useEffect(() => {
    const handleEmmyNavigate = (event) => {
      const screen = event.detail?.screen;
      if (screen && typeof navigateTo === 'function') {
        logger.debug('[App] Received emmyNavigate event, navigating to:', screen);
        navigateTo(screen);
      }
    };
    
    window.addEventListener('emmyNavigate', handleEmmyNavigate);
    return () => {
      window.removeEventListener('emmyNavigate', handleEmmyNavigate);
    };
  }, [navigateTo]);

  // Sync calendar view mode with URL
  useEffect(() => {
    if (currentScreen === 'calendar') {
      // With HashRouter, the hash is in location.hash (e.g., #/calendar/list)
      const hash = location.hash || '';
      const pathParts = hash.split('/').filter(part => part && part !== '#');
      
      // Check if URL contains a view mode (e.g., #/calendar/list)
      if (pathParts[0] === 'calendar' && pathParts[1]) {
        const viewMode = pathParts[1];
        if (['list', 'month', 'week', 'day'].includes(viewMode) && calendarView !== viewMode) {
          setCalendarView(viewMode);
        }
      } else if (pathParts[0] === 'calendar' && !pathParts[1]) {
        // If on /calendar without view mode, default to list and update URL
        if (calendarView !== 'list') {
          setCalendarView('list');
        }
        navigate('/calendar/list', { replace: true });
      }
    }
  }, [location.hash, currentScreen, calendarView, navigate, setCalendarView]);

  // Update URL when calendar view changes (but only if we're on calendar screen)
  useEffect(() => {
    if (currentScreen === 'calendar') {
      const hash = location.hash || '';
      const pathParts = hash.split('/').filter(part => part && part !== '#');
      
      // Only update URL if it doesn't already match the current view
      if (pathParts[0] === 'calendar' && pathParts[1] !== calendarView) {
        navigate(`/calendar/${calendarView}`, { replace: true });
      } else if (pathParts[0] === 'calendar' && !pathParts[1]) {
        navigate(`/calendar/${calendarView}`, { replace: true });
      }
    }
  }, [calendarView, currentScreen, location.hash, navigate]);

  // Search functionality - using extracted hook
  const searchResults = useSearch({
    searchQuery,
    parentReference,
    studyGuides,
    maxResults: 10
  });

  // Question filtering functions (now using utilities from questionUtils)
  const getSpellingWordsByMonth = useCallback((month) => {
    return filterSpellingWordsByMonth(enhancedSpellingWords, month);
  }, [enhancedSpellingWords]);

  const getPhonicsQuestionsByDifficulty = useCallback((difficulty) => {
    return filterPhonicsQuestionsByDifficulty(enhancedPhonicQuestions, difficulty);
  }, [enhancedPhonicQuestions]);

  const getMathQuestionsByDifficulty = useCallback((difficulty) => {
    return filterMathQuestionsByDifficulty(enhancedMathQuestions, difficulty);
  }, [enhancedMathQuestions]);

  const getReadingQuestionsByCategory = useCallback((category) => {
    return filterReadingQuestionsByCategory(enhancedReadingQuestions, category);
  }, [enhancedReadingQuestions]);

  // Calculate total questions for each subject (using utility)
  const getQuestionCountForSubject = useCallback((subject) => {
    return getQuestionCount(subject, {
      enhancedPhonicQuestions,
      enhancedMathQuestions,
      enhancedReadingQuestions,
      enhancedSpellingWords,
      scienceQuestions,
      artQuestions,
      geographyQuestions,
      historyQuestions,
      socialStudiesQuestions,
      skipCountingQuestions,
    });
  }, [enhancedPhonicQuestions, enhancedMathQuestions, enhancedReadingQuestions, enhancedSpellingWords, scienceQuestions, artQuestions, geographyQuestions, historyQuestions, socialStudiesQuestions, skipCountingQuestions]);

  // Prevent multiple answers and control auto-advance
  const [answerLocked, setAnswerLocked] = useState(false);
  const [advanceScheduled, setAdvanceScheduled] = useState(false);
  
  // Refs to store values for keyboard handler (avoids TDZ issues)
  const shuffledOptionsRef = useRef([]);
  const handleAnswerRef = useRef(null);
  const currentScreenRef = useRef(null);
  const answerLockedRef = useRef(false);
  const currentQuestionsRef = useRef(null);
  const currentQuestionRef = useRef(0);
  const showWrongAnswerModalRef = useRef(false);
  const navigateToRef = useRef(null);
  const setCurrentQuestionRef = useRef(null);
  const setShowSearchRef = useRef(null);
  const toggleMusicRef = useRef(null);
  const setShowWrongAnswerModalRef = useRef(null);
  const setWrongAnswerDataRef = useRef(null);
  const setShowPerformanceDashboardRef = useRef(null);
  const showPerformanceDashboardRef = useRef(false);

  // NOTE: scheduleAdvanceToNextQuestion is defined AFTER useQuestionManager 
  // because it depends on currentQuestions which is returned by that hook
  // (see definition below after useQuestionManager hook)

  // Adaptive Learning System
  const calculateAdaptiveDifficulty = (subject, performance) => {
    const currentDifficulty = adaptiveDifficulty[subject] || 'medium';
    const recentScores = performance.slice(-5); // Last 5 attempts
    const averageScore = recentScores.reduce((sum, score) => sum + score, 0) / recentScores.length;
    
    if (averageScore >= SCORE_THRESHOLDS.EXCELLENT && recentScores.length >= RECENT_SCORES_MIN_COUNT) {
      return 'hard';
    } else if (averageScore <= SCORE_THRESHOLDS.NEEDS_IMPROVEMENT && recentScores.length >= RECENT_SCORES_MIN_COUNT) {
      return 'easy';
    }
    return currentDifficulty;
  };

  // Update confidence level using utility function
  const updateConfidenceLevel = (subject, isCorrect, responseTime) => {
    const updatedLevels = updateConfidenceLevelUtil({
      confidenceLevels,
      subject,
      isCorrect,
      responseTime
    });
    
    setConfidenceLevels(updatedLevels);
    
    return updatedLevels[subject];
  };

  // Clean up question history on app start
  useEffect(() => {
    if (progress && progress.questionHistory) {
      cleanupQuestionHistory();
    }
  }, [progress, cleanupQuestionHistory]);

  // Daily Challenge System - using utility
  const generateDailyChallenge = generateDailyChallengeUtil;

  // Learning Streak System - wrapper that updates state
  const updateLearningStreak = useCallback(() => {
    const result = updateLearningStreakUtil(lastLearningDate, learningStreak);
    
    if (result.streakChanged) {
      setLearningStreak(result.newStreak);
      setLastLearningDate(result.newLastDate);
    }
  }, [lastLearningDate, learningStreak]);

  // Feedback System - using utility
  const submitFeedback = useCallback(() => {
    const result = submitFeedbackUtil({
      message: feedbackMessage,
      category: feedbackCategory,
      rating: feedbackRating,
      currentScreen,
      progress,
      existingFeedback: feedback
    });
    
    if (!result.success) {
      return; // Validation failed
    }
    
    // Update feedback state
    setFeedback(result.updatedFeedback);
    
    // Reset form
    const defaults = getDefaultFeedbackValues();
    setFeedbackMessage(defaults.message);
    setFeedbackRating(defaults.rating);
    setFeedbackCategory(defaults.category);
    setShowFeedbackModal(false);
    
    // Show success message
    playSound(SOUND_TYPES.CORRECT);
    triggerHaptic(HAPTIC_TYPES.SUCCESS);
    setShowFeedback(getFeedbackSuccessMessage());
    setTimeout(() => setShowFeedback(null), TIMEOUTS.FEEDBACK_MESSAGE);
  }, [feedbackMessage, feedbackCategory, feedbackRating, currentScreen, progress, feedback, playSound, triggerHaptic]);

  // Memoize feedback stats to prevent recalculation on every render - using utility
  const feedbackStats = useMemo(() => {
    return calculateFeedbackStats(feedback);
  }, [feedback]);

  const getFeedbackStats = () => feedbackStats;

  // Check and unlock achievements - wrapper that handles UI notifications
  const checkAchievements = useCallback((newProgress) => {
    const currentAchievements = newProgress.achievements || [];
    const result = checkAchievementsUtil(newProgress, currentAchievements);
    
    // Show achievement notification if there's exactly one new achievement
    if (result.newAchievements.length === 1) {
      setShowAchievement(achievements[result.newAchievements[0]]);
      setTimeout(() => setShowAchievement(null), TIMEOUTS.ACHIEVEMENT_DISPLAY);
    }
    
    return result.updatedProgress;
  }, [achievements]);

  // Use enhanced science questions from educational content - imported from educationalContent.js
  // No local declaration needed - using imported scienceQuestions from line 59
  // Social Studies and Skip Counting questions now imported from data files (lines 112-113)

  // Enhanced subjects: Art, Geography, History - now imported from educationalContent.js
  // Enhanced subjects: Art, Geography, History - now imported from educationalContent.js
  // Removed local duplicate declarations - using imports from line 59-62 instead

  // Audio feedback (wrapper for new audio system)
  // Note: The actual playSound is now provided by useAudio hook above

  // Background music functions
  const playBackgroundMusic = async () => {
    try {
      // Use audioManager's context if available, otherwise create one only after user interaction
      // Lazy load audioManager to avoid initialization issues
      let ctx = null;
      try {
        const audioManager = await getAudioManager();
        ctx = audioManager?.audioContext;
      } catch (e) {
        // Silently fail
      }
      
      if (!ctx) {
        // Only create new context if audio is initialized (which means user has interacted)
        if (!audioInitialized) {
          // Queue music to start after initialization
          initializeAudio().then(() => {
            if (isMusicPlayingRef.current) {
              playBackgroundMusic();
            }
          });
          return;
        }
        
        // Fallback: create context only if we're sure it's after user interaction
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        if (!AudioContext) return;
        
        // Use options to minimize warnings
        ctx = new AudioContext({
          latencyHint: 'interactive',
          sampleRate: 44100
        });
        
        // Resume if suspended
        if (ctx.state === 'suspended') {
          try {
            await ctx.resume().catch(() => {
              // Silently handle resume errors - expected before user interaction
            });
          } catch (e) {
            // Context may require additional user interaction
            return;
          }
        }
      } else {
        // Ensure the audioManager context is running
        const audioManager = await getAudioManager();
        await audioManager.ensureAudioContextRunning();
        ctx = audioManager.audioContext;
      }
      
      if (!ctx || ctx.state !== 'running') {
        return;
      }
      
      // Create a simple, calming melody
      const melody = [
        { freq: 523, duration: 0.5 }, // C5
        { freq: 659, duration: 0.5 }, // E5
        { freq: 784, duration: 0.5 }, // G5
        { freq: 659, duration: 0.5 }, // E5
        { freq: 523, duration: 0.5 }, // C5
        { freq: 440, duration: 0.5 }, // A4
        { freq: 523, duration: 0.5 }, // C5
        { freq: 440, duration: 1.0 }  // A4
      ];
      
      let currentTime = ctx.currentTime;
      const oscillators = [];
      
      melody.forEach((note, index) => {
        const oscillator = ctx.createOscillator();
        const gainNode = ctx.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(ctx.destination);
        
        oscillator.frequency.value = note.freq;
        oscillator.type = 'sine';
        
        gainNode.gain.setValueAtTime(0.1, currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, currentTime + note.duration);
        
        oscillator.start(currentTime);
        oscillator.stop(currentTime + note.duration);
        
        // Store oscillator reference so we can stop it if needed
        oscillators.push({ oscillator, gainNode, stopTime: currentTime + note.duration });
        
        currentTime += note.duration;
      });
      
      // Store oscillators in ref
      activeOscillatorsRef.current = [...activeOscillatorsRef.current, ...oscillators];
      
      // Loop the melody - use ref to check current state
      const totalDuration = currentTime * 1000;
      musicTimeoutRef.current = setTimeout(() => {
        // Check current state via ref, not closure value
        if (isMusicPlayingRef.current) {
          playBackgroundMusic();
        }
      }, totalDuration);
      
    } catch (e) {
      // Silently fail if audio context is not available
    }
  };

  const stopMusic = useCallback(() => {
    // Clear any pending timeout
    if (musicTimeoutRef.current) {
      clearTimeout(musicTimeoutRef.current);
      musicTimeoutRef.current = null;
    }
    
    // Stop all active oscillators
    activeOscillatorsRef.current.forEach(({ oscillator, gainNode }) => {
      try {
        // Fade out quickly instead of abrupt stop
        if (gainNode && gainNode.gain) {
          const currentTime = gainNode.context.currentTime;
          gainNode.gain.cancelScheduledValues(currentTime);
          gainNode.gain.setValueAtTime(gainNode.gain.value, currentTime);
          gainNode.gain.exponentialRampToValueAtTime(0.001, currentTime + 0.1);
        }
        // Stop oscillator after fade
        if (oscillator) {
          setTimeout(() => {
            try {
              oscillator.stop();
            } catch (e) {
              // Oscillator may already be stopped
            }
          }, 100);
        }
      } catch (e) {
        // Ignore errors - oscillator may already be stopped
      }
    });
    
    // Clear the oscillators array
    activeOscillatorsRef.current = [];
    
    // Clear background music reference
    if (backgroundMusic) {
      try {
        backgroundMusic.stop();
      } catch (e) {
        // Ignore errors
      }
      setBackgroundMusic(null);
    }
  }, [backgroundMusic]);

  const toggleMusic = useCallback(() => {
    if (isMusicPlaying) {
      setIsMusicPlaying(false);
      isMusicPlayingRef.current = false;
      stopMusic();
    } else {
      setIsMusicPlaying(true);
      isMusicPlayingRef.current = true;
      playBackgroundMusic();
    }
  }, [isMusicPlaying, stopMusic]);

  useEffect(() => {
    canvasRefs.forEach((ref) => {
      if (ref.current) {
        const canvas = ref.current;
        const ctx = canvas.getContext('2d');
        
        // Set up high DPI canvas
        const rect = canvas.getBoundingClientRect();
        const dpr = window.devicePixelRatio || 1;
        canvas.width = rect.width * dpr;
        canvas.height = rect.height * dpr;
        ctx.scale(dpr, dpr);
        
        // Set drawing properties
        ctx.strokeStyle = drawColor; 
        ctx.lineWidth = 3;
        ctx.lineCap = 'round'; 
        ctx.lineJoin = 'round';
      }
    });
  }, [currentScreen, drawColor]);

  // Online/offline detection is now handled by useOfflineManager hook

  // Initialize daily challenge, learning streak, and daily login bonus
  useEffect(() => {
    // Check for daily login bonus (using ref to avoid initialization issues)
    const checkDailyBonus = () => {
      if (!gamificationRef.current) return; // Wait for gamification to load
      const lastBonusDate = storage.get('emmy-last-bonus-date');
      const today = new Date().toDateString();
      if (lastBonusDate !== today) {
        const bonus = gamificationRef.current.awardDailyBonus(progress.streak || 0);
        storage.set('emmy-last-bonus-date', today);
        logger.info(`Daily login bonus awarded: ${bonus} coins`);
      }
    };
    // Check after a short delay to ensure gamification module is loaded
    setTimeout(checkDailyBonus, 100);
    
    const challenge = generateDailyChallenge();
    setDailyChallenge(challenge);
    
    // Load learning streak from storage
    const savedStreak = storage.get(STORAGE_KEYS.LEARNING_STREAK);
    const savedLastDate = storage.get(STORAGE_KEYS.LAST_LEARNING_DATE);
    if (savedStreak) setLearningStreak(parseInt(savedStreak));
    if (savedLastDate) setLastLearningDate(savedLastDate);
    
    // Load feedback from storage
    const savedFeedback = storage.get(STORAGE_KEYS.FEEDBACK);
    if (savedFeedback) setFeedback(savedFeedback);
    
    // Load parent mode from storage - default to Kids mode
    const savedParentMode = storage.get(STORAGE_KEYS.PARENT_MODE);
    if (savedParentMode !== null && savedParentMode !== undefined) {
      setParentMode(savedParentMode);
    } else {
      // First time user - default to Kids mode and save it
      setParentMode(false);
      storage.set(STORAGE_KEYS.PARENT_MODE, false);
    }
  }, []);

  // Save learning streak
  useEffect(() => {
    storage.set(STORAGE_KEYS.LEARNING_STREAK, learningStreak.toString());
    if (lastLearningDate) {
      storage.set(STORAGE_KEYS.LAST_LEARNING_DATE, lastLearningDate);
    }
  }, [learningStreak, lastLearningDate]);

  // Save parent mode
  useEffect(() => {
    storage.set(STORAGE_KEYS.PARENT_MODE, parentMode);
  }, [parentMode]);

  // Auto-reset to Kids mode after 30 minutes of inactivity
  useEffect(() => {
    let inactivityTimer;
    
    const resetToKidsMode = () => {
      if (parentMode) {
        setParentMode(false);
        storage.set(STORAGE_KEYS.PARENT_MODE, false);
      }
    };
    
    const resetTimer = () => {
      clearTimeout(inactivityTimer);
      inactivityTimer = setTimeout(resetToKidsMode, TIMEOUTS.INACTIVITY_RESET);
    };
    
    // Reset timer on any user activity
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];
    events.forEach(event => {
      document.addEventListener(event, resetTimer, true);
    });
    
    // Start the timer
    resetTimer();
    
    return () => {
      clearTimeout(inactivityTimer);
      events.forEach(event => {
        document.removeEventListener(event, resetTimer, true);
      });
    };
  }, [parentMode]);

  // Scroll-to-top button visibility
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      setShowScrollToTop(scrollTop > 100); // Reduced threshold for easier testing
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Keyboard shortcuts - use refs to avoid TDZ issues
  useEffect(() => {
    const handleKeyPress = (e) => {
      // Only handle shortcuts when not in input fields
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      
      // Don't interfere with browser shortcuts (Cmd/Ctrl combinations)
      if (e.metaKey || e.ctrlKey) return;
      
      // Use refs to get current values (avoids TDZ and closure issues)
      const currentScreen = currentScreenRef.current;
      const answerLocked = answerLockedRef.current;
      const currentQuestions = currentQuestionsRef.current;
      const currentQuestion = currentQuestionRef.current;
      const showWrongAnswerModal = showWrongAnswerModalRef.current;
      
      // Check if we're in a game screen (playing a question)
      const isGameScreen = ['phonics', 'math', 'reading', 'spelling', 'science', 'social', 'skipcounting', 'art', 'geography', 'history'].includes(currentScreen);
      const isPlayingGame = isGameScreen && !answerLocked && currentQuestions && currentQuestions.length > 0 && currentQuestion < currentQuestions.length;
      
      // A-D keys for answer selection during gameplay
      if (isPlayingGame && ['a', 'b', 'c', 'd', '1', '2', '3', '4'].includes(e.key.toLowerCase())) {
        const key = e.key.toLowerCase();
        const answerMap = {
          'a': 0, '1': 0,
          'b': 1, '2': 1,
          'c': 2, '3': 2,
          'd': 3, '4': 3
        };
        const answerIndex = answerMap[key];
        if (answerIndex !== undefined && currentQuestions && currentQuestions[currentQuestion]) {
          const q = currentQuestions[currentQuestion];
          // Use ref to access current shuffled options (avoids TDZ and closure issues)
          const options = shuffledOptionsRef.current.length > 0 
            ? shuffledOptionsRef.current 
            : (q?.options || []);
          if (options[answerIndex]) {
            e.preventDefault();
            const selectedAnswer = options[answerIndex];
            const correctAnswer = getCorrectAnswer(q);
            // Use ref to access handleAnswer (avoids TDZ issue)
            if (handleAnswerRef.current) {
              handleAnswerRef.current(selectedAnswer, correctAnswer, currentQuestions, q.explanation);
            }
            return;
          }
        }
      }
      
      // Use refs for all function calls
      const navigateTo = navigateToRef.current;
      const setCurrentQuestion = setCurrentQuestionRef.current;
      const setShowSearch = setShowSearchRef.current;
      const toggleMusic = toggleMusicRef.current;
      const setShowWrongAnswerModal = setShowWrongAnswerModalRef.current;
      const setWrongAnswerData = setWrongAnswerDataRef.current;
      const setShowPerformanceDashboard = setShowPerformanceDashboardRef.current;
      const showPerformanceDashboard = showPerformanceDashboardRef.current;
      
      switch(e.key.toLowerCase()) {
        case 'h':
          if (!isPlayingGame && navigateTo) navigateTo('home');
          break;
        case 'p':
          if (!isPlayingGame && navigateTo) navigateTo('parent-reference');
          break;
        case 'a':
          if (!isPlayingGame && navigateTo) navigateTo('achievements');
          break;
        case 'c':
          if (!isPlayingGame && navigateTo) navigateTo('customize');
          break;
        case 'r':
          if (!isPlayingGame && !e.shiftKey && navigateTo) {
            navigateTo('progress');
          }
          break;
        case 's':
          if (!isPlayingGame && setShowSearch) setShowSearch(true);
          break;
        case 'm':
          if (toggleMusic) toggleMusic();
          break;
        case 'escape':
          if (setShowSearch) setShowSearch(false);
          if (showWrongAnswerModal) {
            if (setShowWrongAnswerModal) setShowWrongAnswerModal(false);
            if (setWrongAnswerData) setWrongAnswerData(null);
          }
          break;
        case '1':
          if (!isPlayingGame && navigateTo && setCurrentQuestion) {
            navigateTo('phonics');
            setCurrentQuestion(0);
          }
          break;
        case '2':
          if (!isPlayingGame && navigateTo && setCurrentQuestion) {
            navigateTo('math');
            setCurrentQuestion(0);
          }
          break;
        case '3':
          if (!isPlayingGame && navigateTo && setCurrentQuestion) {
            navigateTo('reading');
            setCurrentQuestion(0);
          }
          break;
        case '4':
          if (!isPlayingGame && navigateTo && setCurrentQuestion) {
            navigateTo('spelling');
            setCurrentQuestion(0);
          }
          break;
        case '5':
          if (!isPlayingGame && navigateTo && setCurrentQuestion) {
            navigateTo('science');
            setCurrentQuestion(0);
          }
          break;
        case '6':
          if (!isPlayingGame && navigateTo && setCurrentQuestion) {
            navigateTo('social');
            setCurrentQuestion(0);
          }
          break;
        case '7':
          if (!isPlayingGame && navigateTo && setCurrentQuestion) {
            navigateTo('art');
            setCurrentQuestion(0);
          }
          break;
        case '8':
          if (!isPlayingGame && navigateTo && setCurrentQuestion) {
            navigateTo('geography');
            setCurrentQuestion(0);
          }
          break;
        case '9':
          if (!isPlayingGame && navigateTo && setCurrentQuestion) {
            navigateTo('history');
            setCurrentQuestion(0);
          }
          break;
        case 'd':
          // Toggle performance dashboard (Ctrl+D or Cmd+D)
          if (e.ctrlKey || e.metaKey) {
            e.preventDefault();
            if (setShowPerformanceDashboard) {
              setShowPerformanceDashboard(!showPerformanceDashboard);
            }
          }
          break;
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, []); // Empty deps - all values accessed via refs

  const startDrawing = (e, idx) => {
    const canvas = canvasRefs[idx].current;
    if (!canvas) return;
    e.preventDefault();
    triggerHaptic('light');
    setIsDrawing(true); setCurrentCanvas(idx);
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    const ctx = canvas.getContext('2d');
    ctx.strokeStyle = drawColor; ctx.lineWidth = 3;
    ctx.beginPath(); ctx.moveTo(x, y);
  };

  const draw = (e, idx) => {
    if (!isDrawing || currentCanvas !== idx) return;
    e.preventDefault();
    const canvas = canvasRefs[idx].current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    const ctx = canvas.getContext('2d');
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  // Clear canvas using utility function
  const clearCanvas = (idx) => {
    if (canvasRefs[idx]) {
      clearCanvasByRef(canvasRefs[idx]);
    }
  };

  const AUTO_ADVANCE_ON_CORRECT = true;
  
  // Load gamification module once at startup to avoid initialization issues
  const gamificationRef = useRef(null);
  useEffect(() => {
    import('./utils/gamification').then(module => {
      gamificationRef.current = module;
    }).catch(err => {
      logger.error('Failed to load gamification module:', err);
    });
  }, []);
  
  const handleAnswer = (sel, cor, qs, explanation) => {
    const startTime = Date.now();
    // Use answer validation utility for comparison
    const ok = compareAnswers(sel, cor);
    // Calculate score using utility function
    const finalPoints = calculatePoints(ok, hintUsed, 10);
    const newScore = score + finalPoints;
    setScore(newScore);
    
    // Reset hint state after scoring
    setHintUsed(false);
    setShowHint(false);
    
    // Track this question in history to avoid repeats
    const currentQ = qs[currentQuestion];
    if (currentQ) {
      const questionId = getQuestionId(currentQ);
      trackQuestionHistory(currentScreen, questionId);
    }
    
    // Update confidence tracking
    const responseTime = Date.now() - startTime;
    const confidence = updateConfidenceLevel(currentScreen, ok, responseTime);
    
    // Track question analytics
    trackQuestionAnswered(
      currentScreen,
      ok,
      responseTime,
      hintUsed ? 2 : 1, // attempt number
      hintUsed
    );
    
    // Update learning streak
    updateStreak();
    
    // Calculate and award coins for correct answers
    let coinsEarned = 0;
    let newCoinBalance = 0;
    if (gamificationRef.current) {
      if (ok) {
        newCoinBalance = gamificationRef.current.getCoins(); // Get current balance before adding
        const isPerfectScore = false; // Will be checked at game completion
        coinsEarned = gamificationRef.current.calculateQuestionCoins(ok, isPerfectScore, progress.streak || 0);
        newCoinBalance = gamificationRef.current.addCoins(coinsEarned, `Correct answer in ${currentScreen}`); // Capture new balance
      } else {
        // Still need to get current balance even for incorrect answers
        newCoinBalance = gamificationRef.current.getCoins();
      }
    }
    
    // Update stats
    const currentStats = progress.stats || {
      totalQuestionsAnswered: 0,
      correctAnswers: 0,
      perfectScores: 0,
      timeSpent: 0,
      favoriteSubject: null
    };
    
    const updatedStats = {
      ...currentStats,
      totalQuestionsAnswered: currentStats.totalQuestionsAnswered + 1,
      correctAnswers: currentStats.correctAnswers + (ok ? 1 : 0),
      timeSpent: currentStats.timeSpent + 1 // Simple time tracking
    };
    
  // Lock answer to prevent multiple submissions
  if (answerLocked) return;
  setAnswerLocked(true);
  
  if (ok) { 
      playSound(SOUND_TYPES.CORRECT);
      triggerHaptic(HAPTIC_TYPES.SUCCESS);
      setShowFeedback('correct'); 
      setAnswerAnimation('correct-bounce');
      
      // Trigger success animation on the question container
      const questionContainer = document.querySelector('.question-container');
      if (questionContainer) {
        animations.triggerSuccess(questionContainer);
      }
    } else { 
      playSound(SOUND_TYPES.INCORRECT);
      triggerHaptic(HAPTIC_TYPES.ERROR);
      setShowFeedback('incorrect'); 
      setAnswerAnimation('incorrect-shake');
      setCorrectAnswer(cor);
      
      // Trigger error animation on the question container
      const questionContainer = document.querySelector('.question-container');
      if (questionContainer) {
        animations.triggerError(questionContainer);
      }
      
      // Track mistake for review
      const currentQ = qs[currentQuestion];
      if (currentQ) {
        const questionId = getQuestionId(currentQ);
        addMistake({
          question: currentQ,
          selectedAnswer: sel,
          correctAnswer: cor,
          explanation: explanation,
          subject: currentScreen,
          questionId: questionId
        });
      }
    }
    
    // Always show solution modal for both correct and incorrect answers
    // Clear any pending advance timers to prevent double-incrementing
    if (advanceTimerRef.current) {
      clearTimeout(advanceTimerRef.current);
      advanceTimerRef.current = null;
    }
    setAdvanceScheduled(false); // Reset advance scheduled flag
    
    setWrongAnswerData({
      selectedAnswer: sel,
      correctAnswer: cor,
      explanation: explanation,
      question: qs[currentQuestion],
      isCorrect: ok,
      coinsEarned: coinsEarned,
      newCoinBalance: newCoinBalance // Pass the new balance directly to avoid race conditions
    });
    setShowWrongAnswerModal(true);
    
    // Only auto-continue for correct answers when enabled
  // Removed old AUTO_ADVANCE_ON_CORRECT branch; handled by scheduleAdvanceToNextQuestion
  };
  
  // Update ref whenever handleAnswer changes (for keyboard handler)
  useEffect(() => {
    handleAnswerRef.current = handleAnswer;
  }, [handleAnswer]);

  // Reset game - wrapper that calls hook's resetGame and adds additional cleanup
  const resetGameComplete = async () => {
    // Track learning progress before resetting
    if (currentScreen && score > 0 && questionCount > 0) {
      const accuracy = calculatePercentage(score, questionCount);
      
      // Track game completion using utility
      await trackGameCompletion({
        currentScreen,
        score,
        questionCount,
        hintUsed
      });
      
      // Update daily challenge progress if applicable
      updateDailyChallengeFromGame({
        dailyChallengeData,
        isChallengeCompleted,
        accuracy,
        updateChallengeProgress
      });
    } else if (currentScreen) {
      // Track drop-off if user exits mid-game
      await trackGameDropOff({
        currentScreen,
        currentQuestion,
        questionCount
      });
    }
    
    resetGame(); // From useGameState hook
    setHintUsed(false);
    setShowHint(false);
    navigateTo('home');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };


  const handleHelpClick = () => {
    if (!hintUsed) {
      setHintUsed(true);
      setShowHint(true);
      playSound(SOUND_TYPES.CLICK);
      triggerHaptic(HAPTIC_TYPES.LIGHT);
      
      // Hide hint after configured timeout
      setTimeout(() => {
        setShowHint(false);
      }, TIMEOUTS.HINT_DISPLAY);
    }
  };

  // Handle game selection with question count
  const handleGameSelection = (gameType) => {
    logger.debug('Game selected:', gameType);
    setPendingGame(gameType);
    // Reset to default (10) when opening modal to ensure selection is visible
    setSelectedQuestionCount(QUESTION_LIMITS.DEFAULT);
    setCustomQuestionCount('');
    setShowQuestionSelector(true);
  };

  // Start game with selected question count (using hook)
  const startGameWithCount = () => {
    const result = startGameWithCountFromHook(() => {
    navigateTo(pendingGame);
    
    // Extra scroll delay for question selector flow to ensure proper mobile experience
    setTimeout(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
      }, TIMEOUTS.SCROLL_BRIEF_DELAY);
    });
    
    if (!result.success) {
      logger.warn('Game start failed:', result.error);
      // Could trigger a toast notification here instead of alert
      setShowFeedback(result.error || 'Unable to start game. Please try again.');
      setTimeout(() => setShowFeedback(null), TIMEOUTS.FEEDBACK_MESSAGE);
    }
  };

  // Question data preparation and shared computed values (must run before render to keep hook order stable)
  const questionSets = {
    phonics: getPhonicsQuestionsByDifficulty(selectedPhonicsDifficulty),
    math: getMathQuestionsByDifficulty(selectedMathDifficulty),
    reading: getReadingQuestionsByCategory(selectedReadingCategory),
    science: scienceQuestions,
    social: [],
    skipcounting: [],
    art: artQuestions,
    geography: geographyQuestions,
    history: historyQuestions
  };

  const questionCount = selectedQuestionCount === 'custom'
    ? parseInt(customQuestionCount, 10) || 10
    : selectedQuestionCount;

  // Question management hook
  const { currentQuestions, fullQuestionSet, smartShuffle: smartShuffleFromHook } = useQuestionManager({
    questionSets,
    currentScreen,
    questionCount,
    progress,
    getQuestionId,
    additionalSets: {
      social: socialStudiesQuestions,
      skipcounting: skipCountingQuestions
    },
    currentQuestion // Pass currentQuestion so hook can detect when game resets
  });

  const qs = currentQuestions;
  const qRaw = (Array.isArray(currentQuestions) && currentQuestions[currentQuestion]) || {};

  // Expand 2-option questions to 4 options for better learning (except true/false)
  // Do this early so we use the expanded question everywhere
  const q = useMemo(() => {
    if (!qRaw || !Array.isArray(qRaw.options)) {
      return qRaw;
    }
    return expandQuestionOptions(qRaw);
  }, [qRaw?.question || qRaw?.word, qRaw?.options?.join?.('|') || '', qRaw?.correct || qRaw?.answer || '']);

  // Ref to store the advance timer so we can clear it if needed
  const advanceTimerRef = useRef(null);
  
  // NOW we can define scheduleAdvanceToNextQuestion since currentQuestions exists
  // NOTE: This function clears showFeedback after delay - FeedbackOverlay will close via its own timer
  // This prevents timing conflicts where we clear feedback before the overlay auto-closes
  const scheduleAdvanceToNextQuestion = useCallback((delayMs = TIMEOUTS.QUESTION_ADVANCE_DELAY) => {
    // Prevent multiple schedules - clear any existing timer first
    if (advanceTimerRef.current) {
      clearTimeout(advanceTimerRef.current);
      advanceTimerRef.current = null;
    }
    
    if (advanceScheduled) return;
    setAdvanceScheduled(true);
    
    advanceTimerRef.current = setTimeout(() => {
      advanceTimerRef.current = null;
      const qs = currentQuestions;
      // Clear feedback state first to ensure overlay closes
      setShowFeedback(null);
      setAnswerAnimation('');
      setShowExplanation(false);
      setCorrectAnswer('');
      
      // Advance or complete
      if (Array.isArray(qs) && currentQuestion < qs.length - 1) {
        // Advance to next question
        setCurrentQuestion(currentQuestion + 1);
      } else if (Array.isArray(qs) && qs.length > 0) {
        // Complete - use scoring utility
        let newProgress = updateProgressWithCompletion(progress, {
          score,
          questionCount,
          currentScreen
        });
        newProgress = checkAchievements(newProgress);
        newProgress = checkUnlocks(newProgress);
        // Save progress (async - syncs to database if logged in)
        saveProgress(newProgress, true).catch(err => {
          // Log but don't block - localStorage already saved
          logger.warn('Progress save completed but sync may have failed:', err);
        });
        playSound(SOUND_TYPES.COMPLETE);
        triggerHaptic(HAPTIC_TYPES.SUCCESS);
        setCurrentScreen('complete');
      }
      
      setAnswerLocked(false);
      // Reset advanceScheduled AFTER we've actually advanced
      setAdvanceScheduled(false);
    }, delayMs);
  }, [advanceScheduled, currentQuestions, currentQuestion, score, questionCount, progress, currentScreen, checkAchievements, checkUnlocks, saveProgress, playSound, triggerHaptic]);
  
  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (advanceTimerRef.current) {
        clearTimeout(advanceTimerRef.current);
      }
    };
  }, []);

  // Safety net: if a correct feedback is showing but for any reason
  // the advance wasn't scheduled (race conditions, interrupted timers),
  // schedule it now to avoid getting stuck on a question.
  // Only run when we're actually in a game screen and have submitted an answer
  useEffect(() => {
    // Only run safety net if:
    // 1. We're in a gameplay screen (not home, complete, etc.)
    // 2. Feedback is showing as correct
    // DISABLED: Auto-advance removed - users now click Continue button in SolutionModal
    // This prevents double-incrementing when user clicks Continue
    // const isGameScreen = ['phonics', 'math', 'reading', 'science', 'art', 'geography', 'history'].includes(currentScreen);
    // if (isGameScreen && showFeedback === 'correct' && !advanceScheduled && answerLocked) {
    //   scheduleAdvanceToNextQuestion(FEEDBACK_TIMEOUTS.CORRECT + FEEDBACK_TIMEOUTS.CLOSE_ANIMATION);
    // }
    // Intentionally NOT including scheduleAdvanceToNextQuestion in deps to prevent loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showFeedback, advanceScheduled, answerLocked, currentScreen]);

  // Memoize the feedback overlay close handler to prevent re-render loops
  // Use refs to avoid including frequently-changing values in deps
  const showFeedbackRef = useRef(showFeedback);
  const advanceScheduledRef = useRef(advanceScheduled);
  const scheduleAdvanceRef = useRef(scheduleAdvanceToNextQuestion);
  useEffect(() => {
    showFeedbackRef.current = showFeedback;
    advanceScheduledRef.current = advanceScheduled;
    scheduleAdvanceRef.current = scheduleAdvanceToNextQuestion;
  }, [showFeedback, advanceScheduled, scheduleAdvanceToNextQuestion]);

  const handleFeedbackClose = useCallback(() => {
    // Close the overlay state using refs to get current values
    const wasCorrect = showFeedbackRef.current === 'correct';
    setShowFeedback(null);
    setAnswerAnimation('');
    setShowExplanation(false);
    setCorrectAnswer('');
    // If this was a correct answer and no advance is scheduled yet, schedule it now
    // This handles manual closes (clicking X or backdrop) or late auto-closes
    if (wasCorrect && !advanceScheduledRef.current) {
      scheduleAdvanceRef.current(0);
    }
  }, []); // Empty deps - use refs instead

  // Memoize shuffled options to prevent constant re-rendering (using utility)
  // Re-shuffle on every question change to ensure random order each time
  // Use question content as dependency to force re-shuffle when question changes
  // q is already expanded at this point, so use it directly
  const shuffledOptions = useMemo(() => {
    if (!q || !Array.isArray(q.options) || q.options.length === 0) {
      return [];
    }
    // Always create a fresh shuffle for each question - ensures random position each time
    // The spread operator creates a new array to avoid reference equality issues
    const optionsCopy = [...q.options];
    return shuffleOptions(optionsCopy);
  }, [currentQuestion, q?.question || q?.word, q?.options?.join?.('|') || '']);
  
  // Update refs whenever values change (for keyboard handler)
  useEffect(() => {
    shuffledOptionsRef.current = shuffledOptions;
  }, [shuffledOptions]);
  
  useEffect(() => {
    currentScreenRef.current = currentScreen;
  }, [currentScreen]);
  
  useEffect(() => {
    answerLockedRef.current = answerLocked;
  }, [answerLocked]);
  
  useEffect(() => {
    currentQuestionsRef.current = currentQuestions;
  }, [currentQuestions]);
  
  useEffect(() => {
    currentQuestionRef.current = currentQuestion;
  }, [currentQuestion]);
  
  useEffect(() => {
    showWrongAnswerModalRef.current = showWrongAnswerModal;
  }, [showWrongAnswerModal]);
  
  useEffect(() => {
    navigateToRef.current = navigateTo;
  }, [navigateTo]);
  
  useEffect(() => {
    setCurrentQuestionRef.current = setCurrentQuestion;
  }, [setCurrentQuestion]);
  
  useEffect(() => {
    setShowSearchRef.current = setShowSearch;
  }, [setShowSearch]);
  
  useEffect(() => {
    toggleMusicRef.current = toggleMusic;
  }, [toggleMusic]);
  
  useEffect(() => {
    setShowWrongAnswerModalRef.current = setShowWrongAnswerModal;
  }, [setShowWrongAnswerModal]);
  
  useEffect(() => {
    setWrongAnswerDataRef.current = setWrongAnswerData;
  }, [setWrongAnswerData]);
  
  useEffect(() => {
    setShowPerformanceDashboardRef.current = setShowPerformanceDashboard;
  }, [setShowPerformanceDashboard]);
  
  useEffect(() => {
    showPerformanceDashboardRef.current = showPerformanceDashboard;
  }, [showPerformanceDashboard]);

  useEffect(() => {
    const scrollTimeout = setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 0);
    return () => clearTimeout(scrollTimeout);
  }, [currentScreen]);

  // Track screen views for analytics
  useEffect(() => {
    if (currentScreen && currentScreen !== 'home') {
      // Lazy load analyticsCollector to avoid initialization issues
      import('./utils/analytics').then(({ analyticsCollector }) => {
        analyticsCollector.trackEvent('screen_view', { 
          screen: currentScreen,
          previousScreen: (process.env.NODE_ENV === 'development' && window.__lastScreen) ? window.__lastScreen : 'unknown'
        });
      }).catch(() => {
        // Silently fail - analytics is not critical
      });
      if (process.env.NODE_ENV === 'development') {
        window.__lastScreen = currentScreen;
      }
    }
  }, [currentScreen]);

  const hasQuestion = q && typeof q === 'object' && (q.question || q.word) && Array.isArray(q.options) && q.options.length > 0;
  
  // Memoize bgColors to prevent recreation on every render
  const bgColors = useMemo(() => ({
    phonics: 'from-pink-200 to-pink-400', 
    math: 'from-blue-200 to-blue-400',
    reading: 'from-green-200 to-green-400', 
    science: 'from-teal-200 to-teal-400',
    social: 'from-orange-200 to-orange-400', 
    skipcounting: 'from-indigo-200 to-indigo-400',
    art: 'from-rose-200 to-rose-400', 
    geography: 'from-emerald-200 to-emerald-400',
    history: 'from-amber-200 to-amber-400'
  }), []);

  // Render function - called AFTER all hooks to prevent hook order violations
  function renderScreen() {
    if (currentScreen === 'home') {
      return (
        <HomeScreen
          key={`home-${progress.avatar}`}
          progress={progress}
          achievements={achievements}
          themes={themes}
          avatars={avatars}
          studyGuides={studyGuides}
          parentReference={parentReference}
          learningStreak={streak}
          dailyChallenge={dailyChallengeData}
          streakMilestone={streakMilestone}
          challengeProgress={challengeProgress}
          challengeProgressPercentage={challengeProgressPercentage}
          isChallengeCompleted={isChallengeCompleted}
          onShowHelp={() => {
            logger.debug('[App] onShowHelp called, setting showHelp to true');
            setShowHelp(true);
          }}
          user={user}
          userProfile={userProfile}
          parentMode={parentMode}
          setParentMode={setParentMode}
          showQuestionSelector={showQuestionSelector}
          setShowQuestionSelector={setShowQuestionSelector}
          selectedQuestionCount={selectedQuestionCount}
          setSelectedQuestionCount={setSelectedQuestionCount}
          customQuestionCount={customQuestionCount}
          setCustomQuestionCount={setCustomQuestionCount}
          pendingGame={pendingGame}
          setPendingGame={setPendingGame}
          startGameWithCount={startGameWithCount}
          showAchievement={showAchievement}
          setShowAchievement={setShowAchievement}
          currentAchievement={currentAchievement}
          showAchievementBadge={showAchievementBadge}
          setShowAchievementBadge={setShowAchievementBadge}
          setCurrentAchievement={setCurrentAchievement}
          showSearch={showSearch}
          setShowSearch={setShowSearch}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          searchResults={searchResults}
          isMusicPlaying={isMusicPlaying}
          toggleMusic={toggleMusic}
          isInitialized={isInitialized}
          loading={loading}
          isOnline={isOnline}
          logout={logout}
          navigateTo={navigateTo}
          handleGameSelection={handleGameSelection}
          getQuestionCountForSubject={getQuestionCountForSubject}
          setCurrentQuestion={setCurrentQuestion}
          setShowProfileManager={setShowProfileManager}
          setShowLegendModal={setShowLegendModal}
          setSelectedSubject={setSelectedSubject}
          playSound={playSound}
          triggerHaptic={triggerHaptic}
          animations={animations}
          currentScreen={currentScreen}
          breadcrumbs={breadcrumbs}
        />
      );
    }

  if (currentScreen.startsWith('guide-')) {
    const type = currentScreen.replace('guide-', '');
    const guide = studyGuides[type];
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-200 via-purple-200 to-pink-200 p-4 md:p-8">
        <div onClick={() => { navigateTo('home'); window.scrollTo({ top: 0, behavior: 'smooth' }); }} className="bg-white text-gray-800 border-2 border-gray-300 px-6 py-3 rounded-full shadow-lg inline-flex gap-2 hover:scale-105 hover:bg-gray-50 hover:border-purple-400 cursor-pointer mb-4 font-semibold transition-all">← Back</div>
        <div className="max-w-3xl mx-auto bg-white rounded-3xl shadow-2xl p-8 md:p-12">
          <div className="text-center mb-8">
            <div className="text-6xl md:text-7xl mb-4">{guide.icon}</div>
            <h1 className="text-3xl md:text-4xl font-bold text-purple-800">{guide.title}</h1>
          </div>
          {guide.sections.map((sec, i) => (
            <div key={i} className="mb-6 bg-gradient-to-br from-purple-50 to-pink-50 p-6 rounded-2xl border-4 border-purple-300">
              <h2 className="text-xl md:text-2xl font-bold text-purple-700 mb-4">{sec.heading}</h2>
              <ul className="space-y-2">
                {sec.items.map((item, j) => (
                  <li key={j} className="text-base md:text-lg text-gray-700 flex gap-2"><span className="text-purple-600 font-bold">•</span><span>{item}</span></li>
                ))}
              </ul>
            </div>
          ))}
          <div className="text-center">
            <div onClick={() => { playSound('click'); setCurrentScreen(type); setCurrentQuestion(0); window.scrollTo({ top: 0, behavior: 'smooth' }); }} 
              className="inline-block px-8 md:px-12 py-4 md:py-6 text-xl md:text-2xl font-bold bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-full shadow-lg hover:scale-110 cursor-pointer">
              Practice Now! 🎮
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (currentScreen === 'spelling') {
    const filteredWords = getSpellingWordsByMonth(selectedSpellingMonth);
    const word = filteredWords[currentQuestion];
    logger.debug('Spelling game render:', { currentQuestion, word, filteredWordsLength: filteredWords.length, selectedMonth: selectedSpellingMonth });
    
    const colors = [
      { name: 'Purple', value: '#8B5CF6' }, { name: 'Pink', value: '#EC4899' },
      { name: 'Blue', value: '#3B82F6' }, { name: 'Green', value: '#10B981' },
      { name: 'Red', value: '#EF4444' }, { name: 'Orange', value: '#F97316' }
    ];

    const monthOptions = [
      { value: 'all', label: 'All Words', count: spellingWords.length },
      { value: 'october', label: 'October', count: 10 },
      { value: 'november', label: 'November', count: 10 },
      { value: 'december', label: 'December', count: 10 },
      { value: 'january', label: 'January', count: 10 },
      { value: 'february', label: 'February', count: 10 },
      { value: 'march', label: 'March', count: 10 },
      { value: 'additional', label: 'Extra Words', count: spellingWords.length - 60 }
    ];
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-200 to-purple-400 p-2 sm:p-4 md:p-8">
        <div onClick={() => { navigateTo('home'); window.scrollTo({ top: 0, behavior: 'smooth' }); }} className="bg-white text-gray-800 border-2 border-gray-300 px-4 sm:px-6 py-2 sm:py-3 rounded-full shadow-lg inline-flex gap-2 hover:scale-105 hover:bg-gray-50 hover:border-purple-400 cursor-pointer mb-3 sm:mb-4 font-semibold transition-all">← Back</div>
        <div className="max-w-4xl mx-auto bg-white rounded-3xl shadow-2xl p-4 sm:p-6 md:p-12">
          
          {/* Month Selector */}
          <div className="mb-6 sm:mb-8">
            <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-purple-700 mb-4 text-center">📅 Choose Your Word List</h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
              {monthOptions.map(option => (
                <button
                  key={option.value}
                  onClick={() => {
                    setSelectedSpellingMonth(option.value);
                    setCurrentQuestion(0);
                    playSound('click');
                    triggerHaptic('light');
                  }}
                  className={`p-3 sm:p-4 rounded-xl font-bold text-sm sm:text-base transition-all transform hover:scale-105 active:scale-95 ${
                    selectedSpellingMonth === option.value
                      ? 'bg-purple-500 text-white shadow-lg'
                      : 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                  }`}
                >
                  <div className="font-bold">{option.label}</div>
                  <div className="text-xs opacity-80">{option.count} words</div>
                </button>
              ))}
            </div>
          </div>
          
          {/* Word Display */}
          <div key={`word-section-${currentQuestion}`} className="text-center mb-6 sm:mb-8">
            <div className="text-3xl sm:text-4xl md:text-5xl mb-3">✏️</div>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 mb-2">Word #{currentQuestion + 1} of {filteredWords.length}</p>
            <div className="text-3xl sm:text-4xl md:text-6xl font-bold text-purple-700 mb-4 sm:mb-6">{word?.word || 'Loading...'}</div>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 italic">Hint: {word?.hint || 'Loading...'}</p>
          </div>
          
          <div className="mb-6 sm:mb-8">
            <p className="text-center text-sm sm:text-base md:text-lg font-bold text-purple-700 mb-3 sm:mb-4">🌈 Choose Rainbow Color!</p>
            <div className="flex justify-center gap-2 sm:gap-3 flex-wrap">
              {colors.map(color => (
                <div key={color.value} onClick={() => { setDrawColor(color.value); triggerHaptic('light'); }}
                  className={`w-10 h-10 sm:w-11 sm:h-11 md:w-12 md:h-12 rounded-full cursor-pointer hover:scale-110 active:scale-95 transition-transform ${drawColor === color.value ? 'ring-4 ring-yellow-400' : ''}`}
                  style={{ backgroundColor: color.value }}
                  role="button"
                  aria-label={`Select ${color.name} color`}
                  tabIndex={0} />
              ))}
            </div>
          </div>
          
          <div key={`canvas-section-${currentQuestion}`} className="bg-gradient-to-br from-yellow-50 to-pink-50 p-3 sm:p-4 md:p-8 rounded-2xl border-4 border-purple-300">
            <p className="text-lg md:text-xl text-center text-purple-700 font-bold mb-4 sm:mb-6">✍️ Write 5 times!</p>
            <div className="space-y-4 sm:space-y-5">
              {[0,1,2,3,4].map(i => (
                <div key={i}>
                  <div className="flex items-center mb-2">
                    <span className="text-xl font-bold text-purple-600 mr-3">{i+1}.</span>
                    <div onClick={() => { clearCanvas(i); triggerHaptic('light'); }} className="ml-auto px-3 py-1 bg-red-400 text-white rounded-full text-sm font-bold cursor-pointer hover:bg-red-500 active:scale-95 transition-transform">Clear</div>
                  </div>
                  <canvas ref={canvasRefs[i]} width={600} height={120}
                    onMouseDown={(e) => startDrawing(e, i)} onMouseMove={(e) => draw(e, i)} 
                    onMouseUp={() => { setIsDrawing(false); setCurrentCanvas(null); }}
                    onTouchStart={(e) => startDrawing(e, i)} onTouchMove={(e) => draw(e, i)} 
                    onTouchEnd={() => { setIsDrawing(false); setCurrentCanvas(null); }}
                    className="w-full h-24 sm:h-28 md:h-32 border-4 border-dashed border-purple-300 rounded-xl bg-white cursor-crosshair touch-none select-none"
                    style={{touchAction: 'none'}}
                    role="img"
                    aria-label={`Drawing canvas ${i+1} for spelling practice`}
                    tabIndex={0} />
                </div>
              ))}
            </div>
          </div>
          <div className="mt-6 sm:mt-8 flex justify-between gap-3 sm:gap-4">
            <div onClick={() => { 
              logger.debug('Prev button clicked!', { currentQuestion });
              if(currentQuestion>0) { 
                logger.debug('Moving to previous question');
                setCurrentQuestion(currentQuestion-1); 
                playSound('click');
                triggerHaptic('light'); 
                // Scroll to top after a brief delay to ensure the new word is rendered
                setTimeout(() => {
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }, TIMEOUTS.SCROLL_BRIEF_DELAY);
              } 
            }} 
              className={`px-4 sm:px-6 py-2 sm:py-3 text-sm sm:text-base font-bold bg-gray-300 rounded-full cursor-pointer active:scale-95 transition-transform ${currentQuestion===0?'opacity-50':''}`}>← Prev</div>
            <div onClick={() => { 
              logger.debug('Next button clicked!', { currentQuestion, filteredWordsLength: filteredWords.length });
              if(currentQuestion<filteredWords.length-1) { 
                logger.debug('Moving to next question');
                setCurrentQuestion(currentQuestion+1); 
                playSound('click');
                triggerHaptic('light'); 
                // Scroll to top after a brief delay to ensure the new word is rendered
                setTimeout(() => {
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }, TIMEOUTS.SCROLL_BRIEF_DELAY);
              } else { 
                logger.debug('Going to home');
                playSound('complete');
                triggerHaptic('success'); 
                navigateTo('home'); 
                window.scrollTo({ top: 0, behavior: 'smooth' }); 
              } 
            }} 
              className="px-4 sm:px-6 py-2 sm:py-3 text-sm sm:text-base font-bold bg-purple-500 text-white rounded-full cursor-pointer active:scale-95 transition-transform">
              {currentQuestion < filteredWords.length-1 ? 'Next →' : 'Done 🎉'}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (currentScreen === 'calendar') {
    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const shortDayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    const currentDate = new Date();
    const currentMonth = selectedCalendarMonth;
    const currentYear = currentDate.getFullYear();
    
    // Helper function to get events for a specific date
    const getEventsForDate = (date) => {
      const month = date.getMonth();
      const day = date.getDate();
      const monthEvents = calendarEvents[month] || [];
      return monthEvents.filter(event => event.date === day);
    };

    // Helper function to split events with dressCode into separate event + dresscode items
    const splitEventsWithDressCode = (events) => {
      const result = [];
      events.forEach(event => {
        // If event has dressCode and it's not already a dresscode type, split it
        if (event.dressCode && event.type !== 'dresscode') {
          // Add the main event WITHOUT the dressCode property - explicitly remove it
          const mainEventWithoutDressCode = {
            date: event.date,
            title: event.title,
            type: event.type,
            description: event.description,
            time: event.time,
            cost: event.cost,
            url: event.url,
            image: event.image
            // Explicitly NOT including dressCode
          };
          result.push(mainEventWithoutDressCode);
          
          // Create a separate dresscode item - keep it simple and separate
          result.push({
            date: event.date,
            type: 'dresscode',
            title: 'Dress Up Day',
            dressCode: event.dressCode
            // No description - dressCode is shown in the purple box
          });
        } else {
          // Event doesn't have dressCode or is already a dresscode type, add as-is
          // But still ensure dressCode is removed if it exists on non-dresscode events
          if (event.type !== 'dresscode' && event.dressCode) {
            const { dressCode, ...cleanEvent } = event;
            delete cleanEvent.dressCode;
            result.push(cleanEvent);
          } else {
            result.push(event);
          }
        }
      });
      return result;
    };

    // Helper function to filter events based on filters
    // Note: dressCode is metadata, not an event type, so we don't filter events by it
    // Instead, we conditionally show/hide dress code display in the UI
    const filterEvents = (events) => {
      return events.filter(event => {
        if (event.type === 'test' && !eventFilters.showTests) return false;
        if (event.type === 'event' && !eventFilters.showEvents) return false;
        if (event.type === 'holiday' && !eventFilters.showHolidays) return false;
        if (event.type === 'dresscode' && !eventFilters.showDressCode) return false;
        return true;
      });
    };

    // Helper function to check if date is today
    const isToday = (date) => {
      const today = new Date();
      return date.getDate() === today.getDate() &&
             date.getMonth() === today.getMonth() &&
             date.getFullYear() === today.getFullYear();
    };

    // Helper function to jump to today
    const jumpToToday = () => {
      const today = new Date();
      setSelectedCalendarMonth(today.getMonth());
      setSelectedDay(new Date(today));
      // Set week start to Sunday of current week
      const day = today.getDay();
      const diff = today.getDate() - day;
      const weekStart = new Date(today);
      weekStart.setDate(diff);
      setSelectedWeekStart(weekStart);
      playSound('click');
      triggerHaptic('light');
    };

    // Get first day of month and number of days
    const firstDay = new Date(currentYear, currentMonth, 1);
    const lastDay = new Date(currentYear, currentMonth + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    // Get events for current month
    const monthEvents = calendarEvents[currentMonth] || [];

    // Create calendar grid for month view
    const calendarDays = [];
    
    // Add empty cells for days before month starts
    for (let i = 0; i < startingDayOfWeek; i++) {
      calendarDays.push({ day: '', isEmpty: true });
    }
    
    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentYear, currentMonth, day);
      const rawEvents = getEventsForDate(date);
      const eventsWithDressCodes = splitEventsWithDressCode(rawEvents);
      const dayEvents = filterEvents(eventsWithDressCodes);
      calendarDays.push({ 
        day, 
        date,
        isEmpty: false, 
        events: dayEvents,
        isToday: isToday(date)
      });
    }

    // Create week view data
    const weekDays = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(selectedWeekStart);
      date.setDate(selectedWeekStart.getDate() + i);
      const rawEvents = getEventsForDate(date);
      const eventsWithDressCodes = splitEventsWithDressCode(rawEvents);
      const dayEvents = filterEvents(eventsWithDressCodes);
      weekDays.push({
        date,
        day: date.getDate(),
        month: date.getMonth(),
        year: date.getFullYear(),
        dayName: dayNames[date.getDay()],
        shortDayName: shortDayNames[date.getDay()],
        events: dayEvents,
        isToday: isToday(date)
      });
    }

    // Get day view data
    const rawDayEvents = getEventsForDate(selectedDay);
    const dayEventsWithDressCodes = splitEventsWithDressCode(rawDayEvents);
    const dayEvents = filterEvents(dayEventsWithDressCodes);

    const getEventTypeColor = (type) => {
      switch (type) {
        case 'test': return 'bg-red-100 text-red-800 border-red-200';
        case 'event': return 'bg-blue-100 text-blue-800 border-blue-200';
        case 'holiday': return 'bg-green-100 text-green-800 border-green-200';
        case 'dresscode': return 'bg-purple-100 text-purple-800 border-purple-200';
        default: return 'bg-gray-100 text-gray-800 border-gray-200';
      }
    };

    const getEventTypeIcon = (type) => {
      switch (type) {
        case 'test': return '📝';
        case 'event': return '🎉';
        case 'holiday': return '🏫';
        case 'dresscode': return '👕';
        default: return '📅';
      }
    };

    // Navigation functions
    const navigateWeek = (direction) => {
      const newWeekStart = new Date(selectedWeekStart);
      newWeekStart.setDate(selectedWeekStart.getDate() + (direction * 7));
      setSelectedWeekStart(newWeekStart);
      setSelectedCalendarMonth(newWeekStart.getMonth());
      playSound('click');
      triggerHaptic('light');
    };

    const navigateDay = (direction) => {
      const newDay = new Date(selectedDay);
      newDay.setDate(selectedDay.getDate() + direction);
      setSelectedDay(newDay);
      setSelectedCalendarMonth(newDay.getMonth());
      playSound('click');
      triggerHaptic('light');
    };

    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-200 to-teal-400 p-4 md:p-8">
        <style>{`
          /* Force button colors to stay visible on click - override all browser defaults */
          /* Target all calendar buttons with teal background */
          button.bg-teal-500,
          button[class*="bg-teal-500"] {
            -webkit-tap-highlight-color: transparent !important;
            user-select: none !important;
            -webkit-user-select: none !important;
          }
          button.bg-teal-500:active,
          button.bg-teal-500:focus:active,
          button.bg-teal-500:focus,
          button[class*="bg-teal-500"]:active,
          button[class*="bg-teal-500"]:focus:active,
          button[class*="bg-teal-500"]:focus,
          button[class*="bg-teal-500"]:active:focus,
          button[class*="bg-teal-500"]:active:hover {
            background-color: #0d9488 !important;
            background: #0d9488 !important;
            color: white !important;
            border-color: transparent !important;
            outline: none !important;
            -webkit-tap-highlight-color: transparent !important;
          }
          button.bg-teal-500:hover,
          button[class*="bg-teal-500"]:hover {
            background-color: #0d9488 !important;
            background: #0d9488 !important;
          }
          
          /* Orange buttons (Today button) */
          button.bg-orange-500,
          button[class*="bg-orange-500"] {
            -webkit-tap-highlight-color: transparent !important;
            user-select: none !important;
            -webkit-user-select: none !important;
          }
          button.bg-orange-500:active,
          button.bg-orange-500:focus:active,
          button.bg-orange-500:focus,
          button[class*="bg-orange-500"]:active,
          button[class*="bg-orange-500"]:focus:active,
          button[class*="bg-orange-500"]:focus,
          button[class*="bg-orange-500"]:active:focus,
          button[class*="bg-orange-500"]:active:hover {
            background-color: #ea580c !important;
            background: #ea580c !important;
            color: white !important;
            border-color: transparent !important;
            outline: none !important;
            -webkit-tap-highlight-color: transparent !important;
          }
          button.bg-orange-500:hover,
          button[class*="bg-orange-500"]:hover {
            background-color: #ea580c !important;
            background: #ea580c !important;
          }
          
          /* Gray buttons (unselected view toggles) */
          button.bg-gray-200,
          button[class*="bg-gray-200"] {
            -webkit-tap-highlight-color: transparent !important;
            user-select: none !important;
            -webkit-user-select: none !important;
          }
          button.bg-gray-200:active,
          button.bg-gray-200:focus:active,
          button.bg-gray-200:focus,
          button[class*="bg-gray-200"]:active,
          button[class*="bg-gray-200"]:focus:active,
          button[class*="bg-gray-200"]:focus,
          button[class*="bg-gray-200"]:active:focus,
          button[class*="bg-gray-200"]:active:hover {
            background-color: #d1d5db !important;
            background: #d1d5db !important;
            color: #374151 !important;
            border-color: transparent !important;
            outline: none !important;
            -webkit-tap-highlight-color: transparent !important;
          }
          button.bg-gray-200:hover,
          button[class*="bg-gray-200"]:hover {
            background-color: #d1d5db !important;
            background: #d1d5db !important;
          }
          
          /* Prevent all default button styling */
          button {
            -webkit-tap-highlight-color: transparent !important;
            user-select: none !important;
            -webkit-user-select: none !important;
            -webkit-appearance: none !important;
            appearance: none !important;
          }
          
          /* Override any browser default button styles */
          button::-moz-focus-inner {
            border: 0 !important;
            padding: 0 !important;
          }
        `}</style>
        <div onClick={() => { navigateTo('home'); window.scrollTo({ top: 0, behavior: 'smooth' }); }} 
          className="bg-white text-gray-800 border-2 border-gray-300 px-4 sm:px-6 py-2 sm:py-3 rounded-full shadow-lg inline-flex gap-2 hover:scale-105 hover:bg-gray-50 hover:border-purple-400 cursor-pointer mb-4 font-semibold transition-all">← Back</div>
        
        <div className="max-w-6xl mx-auto bg-white rounded-3xl shadow-2xl p-6 md:p-12">
          <div className="text-center mb-8">
            <div className="text-6xl mb-4">📅</div>
            <h1 className="text-3xl md:text-4xl font-bold text-teal-700 mb-2">School Calendar</h1>
            <p className="text-lg text-gray-600">Important dates and events from Smore newsletters</p>
          </div>

          {/* View Toggle and Jump to Today */}
          <div className="flex flex-wrap justify-between items-center mb-6 gap-4">
            <div className="flex gap-2">
              <button
                onClick={() => { 
                  setCalendarView('list'); 
                  navigate('/calendar/list', { replace: true });
                  playSound('click'); 
                  triggerHaptic('light'); 
                }}
                className={`px-4 py-2 rounded-full font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-teal-300 ${
                  calendarView === 'list' 
                    ? 'bg-teal-500 text-white hover:bg-teal-600 active:bg-teal-600' 
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300 active:bg-gray-300'
                }`}
              >
                List
              </button>
              <button
                onClick={() => { 
                  setCalendarView('month'); 
                  navigate('/calendar/month', { replace: true });
                  playSound('click'); 
                  triggerHaptic('light'); 
                }}
                className={`px-4 py-2 rounded-full font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-teal-300 ${
                  calendarView === 'month' 
                    ? 'bg-teal-500 text-white hover:bg-teal-600 active:bg-teal-600' 
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300 active:bg-gray-300'
                }`}
              >
                Month
              </button>
              <button
                onClick={() => { 
                  setCalendarView('week'); 
                  navigate('/calendar/week', { replace: true });
                  playSound('click'); 
                  triggerHaptic('light'); 
                }}
                className={`px-4 py-2 rounded-full font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-teal-300 ${
                  calendarView === 'week' 
                    ? 'bg-teal-500 text-white hover:bg-teal-600 active:bg-teal-600' 
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300 active:bg-gray-300'
                }`}
              >
                Week
              </button>
              <button
                onClick={() => { 
                  setCalendarView('day'); 
                  navigate('/calendar/day', { replace: true });
                  playSound('click'); 
                  triggerHaptic('light'); 
                }}
                className={`px-4 py-2 rounded-full font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-teal-300 ${
                  calendarView === 'day' 
                    ? 'bg-teal-500 text-white hover:bg-teal-600 active:bg-teal-600' 
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300 active:bg-gray-300'
                }`}
              >
                Day
              </button>
            </div>
            <button
              onClick={jumpToToday}
              className="px-4 py-2 bg-orange-500 rounded-full hover:bg-orange-600 active:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-300 transition-colors font-semibold shadow-lg"
              style={{ color: '#ffffff', textShadow: '0 1px 2px rgba(0,0,0,0.2)' }}
            >
              📍 Today
            </button>
          </div>

          {/* Event Filters */}
          <div className="bg-gradient-to-r from-teal-50 to-cyan-50 rounded-2xl p-5 mb-6 shadow-lg border border-teal-100">
            <div className="flex items-center gap-3 mb-3">
              <span className="text-xl">🔍</span>
              <h3 className="font-bold text-teal-800 text-lg">Filter Events</h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <label className="flex items-start gap-3 cursor-pointer bg-white px-4 py-3 rounded-xl border-2 border-red-200 hover:border-red-300 hover:shadow-md transition-all group">
                <input
                  type="checkbox"
                  checked={eventFilters.showTests}
                  onChange={(e) => setEventFilters({...eventFilters, showTests: e.target.checked})}
                  className="w-5 h-5 mt-0.5 text-red-600 focus:ring-red-500 focus:ring-2 rounded"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-2xl">📝</span>
                    <span className="font-bold text-red-700 text-base">Tests</span>
                  </div>
                  <p className="text-xs text-gray-600 leading-tight">Assessments & Quizzes</p>
                </div>
              </label>
              <label className="flex items-start gap-3 cursor-pointer bg-white px-4 py-3 rounded-xl border-2 border-blue-200 hover:border-blue-300 hover:shadow-md transition-all group">
                <input
                  type="checkbox"
                  checked={eventFilters.showEvents}
                  onChange={(e) => setEventFilters({...eventFilters, showEvents: e.target.checked})}
                  className="w-5 h-5 mt-0.5 text-blue-600 focus:ring-blue-500 focus:ring-2 rounded"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-2xl">🎉</span>
                    <span className="font-bold text-blue-700 text-base">Events</span>
                  </div>
                  <p className="text-xs text-gray-600 leading-tight">Field Trips & Activities</p>
                </div>
              </label>
              <label className="flex items-start gap-3 cursor-pointer bg-white px-4 py-3 rounded-xl border-2 border-green-200 hover:border-green-300 hover:shadow-md transition-all group">
                <input
                  type="checkbox"
                  checked={eventFilters.showHolidays}
                  onChange={(e) => setEventFilters({...eventFilters, showHolidays: e.target.checked})}
                  className="w-5 h-5 mt-0.5 text-green-600 focus:ring-green-500 focus:ring-2 rounded"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-2xl">🏫</span>
                    <span className="font-bold text-green-700 text-base">Holidays</span>
                  </div>
                  <p className="text-xs text-gray-600 leading-tight">No School Days</p>
                </div>
              </label>
              <label className="flex items-start gap-3 cursor-pointer bg-white px-4 py-3 rounded-xl border-2 border-purple-200 hover:border-purple-300 hover:shadow-md transition-all group">
                <input
                  type="checkbox"
                  checked={eventFilters.showDressCode}
                  onChange={(e) => setEventFilters({...eventFilters, showDressCode: e.target.checked})}
                  className="w-5 h-5 mt-0.5 text-purple-600 focus:ring-purple-500 focus:ring-2 rounded"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-2xl">👕</span>
                    <span className="font-bold text-purple-700 text-base">Dress Up Days</span>
                  </div>
                  <p className="text-xs text-gray-600 leading-tight">What to Wear</p>
                </div>
              </label>
            </div>
          </div>

          {/* Month View */}
          {calendarView === 'month' && (
            <>
              {/* Month Navigation */}
              <div className="flex justify-between items-center mb-6">
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    setSelectedCalendarMonth(selectedCalendarMonth === 0 ? 11 : selectedCalendarMonth - 1);
                    playSound('click');
                    triggerHaptic('light');
                  }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.currentTarget.style.backgroundColor = '#14b8a6';
                    e.currentTarget.style.color = 'white';
                  }}
                  onMouseUp={(e) => {
                    e.currentTarget.style.backgroundColor = '';
                    e.currentTarget.style.color = '';
                  }}
                  className="px-4 py-2 bg-teal-500 text-white rounded-full hover:bg-teal-600 focus:outline-none focus:ring-2 focus:ring-teal-300 transition-colors"
                  style={{ WebkitTapHighlightColor: 'transparent', userSelect: 'none' }}
                >
                  ← Previous
                </button>
                
                <h2 className="text-2xl md:text-3xl font-bold text-teal-700">
                  {monthNames[currentMonth]} {currentYear}
                </h2>
                
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    setSelectedCalendarMonth(selectedCalendarMonth === 11 ? 0 : selectedCalendarMonth + 1);
                    playSound('click');
                    triggerHaptic('light');
                  }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.currentTarget.style.backgroundColor = '#14b8a6';
                    e.currentTarget.style.color = 'white';
                  }}
                  onMouseUp={(e) => {
                    e.currentTarget.style.backgroundColor = '';
                    e.currentTarget.style.color = '';
                  }}
                  className="px-4 py-2 bg-teal-500 text-white rounded-full hover:bg-teal-600 focus:outline-none focus:ring-2 focus:ring-teal-300 transition-colors"
                  style={{ WebkitTapHighlightColor: 'transparent', userSelect: 'none' }}
                >
                  Next →
                </button>
              </div>

              {/* Calendar Grid */}
              <div className="grid grid-cols-7 gap-2 mb-6">
                {shortDayNames.map(day => (
                  <div key={day} className="p-3 text-center font-bold text-teal-600 bg-teal-50 rounded-lg">
                    {day}
                  </div>
                ))}
                
                {calendarDays.map((dayData, index) => (
                  <div
                    key={index}
                    onClick={() => {
                      if (!dayData.isEmpty && dayData.date) {
                        setSelectedDay(dayData.date);
                        setCalendarView('day');
                        playSound('click');
                        triggerHaptic('light');
                      }
                    }}
                    className={`min-h-[120px] p-2 border rounded-lg flex flex-col cursor-pointer transition-all ${
                      dayData.isEmpty 
                        ? 'bg-gray-50 cursor-default' 
                        : dayData.isToday 
                          ? 'bg-teal-100 border-teal-300 ring-2 ring-teal-400' 
                          : 'bg-white border-gray-200 hover:bg-teal-50 hover:shadow-md'
                    }`}
                  >
                    {!dayData.isEmpty && (
                      <>
                        <div className={`text-sm font-bold mb-2 ${
                          dayData.isToday ? 'text-teal-700' : 'text-gray-700'
                        }`}>
                          {dayData.day}
                        </div>
                        
                        <div className="flex-1 flex flex-col gap-1 overflow-hidden">
                          {dayData.events.slice(0, 3).map((event, eventIndex) => (
                            <div
                              key={eventIndex}
                              className={`text-xs p-1 rounded border ${getEventTypeColor(event.type)} flex-shrink-0`}
                              title={event.description + (event.dressCode && eventFilters.showDressCode ? ` | 👕 ${event.dressCode}` : '')}
                            >
                              <div className="flex items-start gap-1">
                                <span className="flex-shrink-0">{getEventTypeIcon(event.type)}</span>
                                <div className="flex-1 min-w-0">
                                  <div className="break-words leading-tight">{event.title}</div>
                                  {event.type === 'dresscode' && event.dressCode && (
                                    <div className="text-[9px] sm:text-[10px] font-bold text-purple-800 mt-0.5 leading-tight">{event.dressCode}</div>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                          {dayData.events.length > 3 && (
                            <div className="text-xs text-gray-500 font-semibold">
                              +{dayData.events.length - 3} more
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Week View */}
          {calendarView === 'week' && (
            <>
              {/* Week Navigation */}
              <div className="flex justify-between items-center mb-6">
                <button
                  onClick={(e) => { e.preventDefault(); navigateWeek(-1); }}
                  onMouseDown={(e) => e.preventDefault()}
                  className="px-4 py-2 bg-teal-500 text-white rounded-full hover:bg-teal-600 focus:outline-none focus:ring-2 focus:ring-teal-300 transition-colors"
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  ← Previous Week
                </button>
                
                <h2 className="text-xl md:text-2xl font-bold text-teal-700 text-center">
                  {monthNames[weekDays[0].month]} {weekDays[0].day} - {monthNames[weekDays[6].month]} {weekDays[6].day}, {weekDays[0].year}
                </h2>
                
                <button
                  onClick={(e) => { e.preventDefault(); navigateWeek(1); }}
                  onMouseDown={(e) => e.preventDefault()}
                  className="px-4 py-2 bg-teal-500 text-white rounded-full hover:bg-teal-600 focus:outline-none focus:ring-2 focus:ring-teal-300 transition-colors"
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  Next Week →
                </button>
              </div>

              {/* Week Grid */}
              <div className="grid grid-cols-1 md:grid-cols-7 gap-3 mb-6 auto-cols-fr">
                {weekDays.map((dayData, index) => (
                  <div
                    key={index}
                    onClick={() => {
                      setSelectedDay(dayData.date);
                      setCalendarView('day');
                      playSound('click');
                      triggerHaptic('light');
                    }}
                    className={`min-h-[300px] min-w-0 p-3 border rounded-lg flex flex-col cursor-pointer transition-all ${
                      dayData.isToday 
                        ? 'bg-teal-100 border-teal-300 ring-2 ring-teal-400' 
                        : 'bg-white border-gray-200 hover:bg-teal-50 hover:shadow-md'
                    }`}
                  >
                    <div className={`text-center mb-3 pb-2 border-b ${
                      dayData.isToday ? 'border-teal-300' : 'border-gray-200'
                    }`}>
                      <div className="text-xs text-gray-500 font-semibold">{dayData.shortDayName}</div>
                      <div className={`text-2xl font-bold ${
                        dayData.isToday ? 'text-teal-700' : 'text-gray-700'
                      }`}>
                        {dayData.day}
                      </div>
                      <div className="text-xs text-gray-500">{monthNames[dayData.month]}</div>
                    </div>
                    
                    <div className="flex-1 flex flex-col gap-2 overflow-y-auto">
                      {dayData.events.length === 0 ? (
                        <div className="text-xs text-gray-400 text-center py-4">No events</div>
                      ) : (
                        dayData.events.map((event, eventIndex) => (
                          <div
                            key={eventIndex}
                            className={`text-sm p-2 rounded border ${getEventTypeColor(event.type)} flex-shrink-0`}
                            title={event.description}
                          >
                            <div className="flex items-start gap-2">
                              <span className="text-base flex-shrink-0">{getEventTypeIcon(event.type)}</span>
                              <div className="flex-1 min-w-0">
                                <div className="font-semibold break-words leading-tight" style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>{event.title}</div>
                                {event.type === 'dresscode' && event.dressCode && (
                                  <div className="text-xs mt-1 font-bold text-purple-800 break-words leading-tight" style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                                    {event.dressCode}
                                  </div>
                                )}
                                {event.description && (
                                  <div className="text-xs mt-1 opacity-75 break-words leading-tight" style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>{event.description}</div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Day View */}
          {calendarView === 'day' && (
            <>
              {/* Day Navigation */}
              <div className="flex justify-between items-center mb-6">
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    navigateDay(-1);
                  }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.currentTarget.style.backgroundColor = '#14b8a6';
                    e.currentTarget.style.color = 'white';
                  }}
                  onMouseUp={(e) => {
                    e.currentTarget.style.backgroundColor = '';
                    e.currentTarget.style.color = '';
                  }}
                  className="px-4 py-2 bg-teal-500 text-white rounded-full hover:bg-teal-600 focus:outline-none focus:ring-2 focus:ring-teal-300 transition-colors"
                  style={{ WebkitTapHighlightColor: 'transparent', userSelect: 'none' }}
                >
                  ← Previous Day
                </button>
                
                <h2 className="text-xl md:text-2xl font-bold text-teal-700 text-center">
                  {dayNames[selectedDay.getDay()]}, {monthNames[selectedDay.getMonth()]} {selectedDay.getDate()}, {selectedDay.getFullYear()}
                </h2>
                
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    navigateDay(1);
                  }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.currentTarget.style.backgroundColor = '#14b8a6';
                    e.currentTarget.style.color = 'white';
                  }}
                  onMouseUp={(e) => {
                    e.currentTarget.style.backgroundColor = '';
                    e.currentTarget.style.color = '';
                  }}
                  className="px-4 py-2 bg-teal-500 text-white rounded-full hover:bg-teal-600 focus:outline-none focus:ring-2 focus:ring-teal-300 transition-colors"
                  style={{ WebkitTapHighlightColor: 'transparent', userSelect: 'none' }}
                >
                  Next Day →
                </button>
              </div>

              {/* Day Events */}
              <div className="bg-white rounded-xl border-2 border-teal-200 p-6 mb-6">
                {dayEvents.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="text-6xl mb-4">📅</div>
                    <h3 className="text-xl font-bold text-gray-700 mb-2">No Events Scheduled</h3>
                    <p className="text-gray-500">This day has no events in the calendar.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <h3 className="text-2xl font-bold text-teal-700 mb-4">
                      {dayEvents.length} Event{dayEvents.length !== 1 ? 's' : ''} Today
                    </h3>
                    {(() => {
                      // Sort events: events first, then dress codes
                      const sortedDayEvents = [...dayEvents].sort((a, b) => {
                        const typeOrder = { event: 0, test: 1, dresscode: 2, holiday: 3 };
                        return (typeOrder[a.type] || 99) - (typeOrder[b.type] || 99);
                      });
                      
                      return sortedDayEvents.map((event, index) => {
                        return (
                          <div
                            key={index}
                            className={`p-4 rounded-xl border-2 ${getEventTypeColor(event.type)}`}
                          >
                            <div className="flex items-start gap-3">
                              <span className="text-3xl">{getEventTypeIcon(event.type)}</span>
                              <div className="flex-1">
                                <h4 className="text-lg font-bold mb-2">{event.title}</h4>
                                {event.type === 'dresscode' && event.dressCode && (
                                  <div className="text-base font-bold text-purple-800 mb-2 whitespace-pre-line">
                                    {event.dressCode}
                                  </div>
                                )}
                                {event.time && (
                                  <div className="text-sm font-semibold text-gray-700 mb-1">
                                    ⏰ {event.time}
                                  </div>
                                )}
                                {event.cost && (
                                  <div className="text-sm font-semibold text-gray-700 mb-1">
                                    💰 {event.cost}
                                  </div>
                                )}
                                {event.description && event.type !== 'dresscode' && (
                                  <p className="text-sm opacity-90 mb-2 whitespace-pre-line">{event.description}</p>
                                )}
                                {event.image && (
                                  <div className="mb-3">
                                    <img 
                                      src={event.image} 
                                      alt={event.title}
                                      className="w-full max-w-md rounded-lg shadow-md border-2 border-gray-200"
                                      onError={(e) => {
                                        e.target.style.display = 'none';
                                      }}
                                    />
                                  </div>
                                )}
                                {event.url && (
                                  <a 
                                    href={event.url} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="inline-block mt-2 px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors font-semibold text-sm"
                                  >
                                    🔗 Get Tickets / More Info
                                  </a>
                                )}
                                {event.type !== 'dresscode' && (
                                  <div className="mt-2 text-xs font-semibold opacity-75">
                                    {event.type === 'test' ? '📝 Assessment' : 
                                     event.type === 'event' ? '🎉 School Event' : 
                                     '🏫 Holiday / No School'}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      });
                    })()}
                  </div>
                )}
              </div>
            </>
          )}

          {/* List View */}
          {calendarView === 'list' && (() => {
            // Collect all events from all months and sort chronologically
            const allEvents = [];
            const today = new Date();
            today.setHours(0, 0, 0, 0); // Set to start of day for accurate comparison
            
            // Get current year, month, and day
            const currentYear = today.getFullYear();
            const currentMonthIndex = today.getMonth();
            const currentDay = today.getDate();
            
            // First, check remaining months in current year (from current month onwards)
            for (let month = currentMonthIndex; month < 12; month++) {
              const monthEvents = calendarEvents[month] || [];
              const eventsWithDressCodes = splitEventsWithDressCode(monthEvents);
              eventsWithDressCodes.forEach(event => {
                // For current month, only include events from today onwards
                // For future months in current year, include all events
                if (month === currentMonthIndex && event.date < currentDay) {
                  return; // Skip past events in current month
                }
                
                const eventDate = new Date(currentYear, month, event.date);
                eventDate.setHours(0, 0, 0, 0);
                
                // Only include future events (from today onwards)
                if (eventDate >= today) {
                  allEvents.push({
                    ...event,
                    fullDate: eventDate,
                    month,
                    monthName: monthNames[month],
                    year: currentYear,
                    isPast: false
                  });
                }
              });
            }
            
            // Then, check all months in next year
            const nextYear = currentYear + 1;
            for (let month = 0; month < 12; month++) {
              const monthEvents = calendarEvents[month] || [];
              const eventsWithDressCodes = splitEventsWithDressCode(monthEvents);
              eventsWithDressCodes.forEach(event => {
                const eventDate = new Date(nextYear, month, event.date);
                eventDate.setHours(0, 0, 0, 0);
                
                // All next year events are in the future
                allEvents.push({
                  ...event,
                  fullDate: eventDate,
                  month,
                  monthName: monthNames[month],
                  year: nextYear,
                  isPast: false
                });
              });
            }
            
            // Sort by date (ascending - earliest first)
            allEvents.sort((a, b) => {
              if (a.fullDate.getTime() !== b.fullDate.getTime()) {
                return a.fullDate.getTime() - b.fullDate.getTime();
              }
              // If same date, sort by type (tests first, then events, then holidays)
              const typeOrder = { test: 0, event: 1, holiday: 2 };
              return (typeOrder[a.type] || 3) - (typeOrder[b.type] || 3);
            });
            
            // Filter events based on active filters
            const filteredListEvents = filterEvents(allEvents);
            
            // Group by month, then by day for better organization
            const eventsByMonth = {};
            filteredListEvents.forEach(event => {
              const eventYear = event.year || event.fullDate.getFullYear();
              const monthKey = `${event.monthName} ${eventYear}`;
              const dayKey = `${event.date}`;
              
              if (!eventsByMonth[monthKey]) {
                eventsByMonth[monthKey] = {};
              }
              if (!eventsByMonth[monthKey][dayKey]) {
                eventsByMonth[monthKey][dayKey] = [];
              }
              eventsByMonth[monthKey][dayKey].push(event);
            });
            
            // Sort month keys chronologically (by year and month index)
            const sortedMonthKeys = Object.keys(eventsByMonth).sort((a, b) => {
              // Extract year and month name from keys like "December 2025"
              const [monthA, yearA] = a.split(' ');
              const [monthB, yearB] = b.split(' ');
              const yearNumA = parseInt(yearA);
              const yearNumB = parseInt(yearB);
              
              // First compare by year
              if (yearNumA !== yearNumB) {
                return yearNumA - yearNumB;
              }
              
              // If same year, compare by month index
              const monthIndexA = monthNames.indexOf(monthA);
              const monthIndexB = monthNames.indexOf(monthB);
              return monthIndexA - monthIndexB;
            });
            
            return (
              <>
                <div className="mb-6">
                  <h2 className="text-2xl font-bold text-teal-700 mb-4">📋 All Upcoming Events</h2>
                  <p className="text-gray-600">Chronological list of all calendar events</p>
                </div>
                
                {filteredListEvents.length === 0 ? (
                  <div className="bg-white rounded-xl border-2 border-teal-200 p-12 text-center">
                    <div className="text-6xl mb-4">📅</div>
                    <h3 className="text-xl font-bold text-gray-700 mb-2">No Events Found</h3>
                    <p className="text-gray-500">Try adjusting your filters to see more events.</p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {sortedMonthKeys.map(monthKey => {
                      const monthEvents = eventsByMonth[monthKey];
                      const sortedDays = Object.keys(monthEvents).sort((a, b) => parseInt(a) - parseInt(b));
                      
                      return (
                        <div key={monthKey} className="bg-white rounded-xl border-2 border-teal-200 p-6">
                          <h3 className="text-xl font-bold text-teal-700 mb-4 pb-2 border-b border-teal-200">
                            {monthKey}
                          </h3>
                          <div className="space-y-4">
                            {sortedDays.map(dayKey => {
                              const dayEvents = monthEvents[dayKey];
                              const firstEvent = dayEvents[0];
                              const isTodayEvent = isToday(firstEvent.fullDate);
                              const dayName = dayNames[firstEvent.fullDate.getDay()];
                              
                              return (
                                <div
                                  key={`${monthKey}-${dayKey}`}
                                  className={`rounded-xl border-2 ${
                                    isTodayEvent ? 'border-teal-400 bg-teal-50' : 'border-gray-200 bg-gray-50'
                                  } overflow-hidden`}
                                >
                                  {/* Day Header */}
                                  <div className={`px-4 py-3 border-b-2 ${
                                    isTodayEvent ? 'bg-teal-100 border-teal-300' : 'bg-white border-gray-200'
                                  }`}>
                                    <div className="flex items-center gap-3">
                                      <div className={`text-3xl font-bold ${isTodayEvent ? 'text-teal-700' : 'text-gray-700'}`}>
                                        {dayKey}
                                      </div>
                                      <div className="flex-1">
                                        <div className={`text-lg font-bold ${isTodayEvent ? 'text-teal-700' : 'text-gray-700'}`}>
                                          {dayName}
                                        </div>
                                        <div className="text-xs text-gray-500">
                                          {dayEvents.length} event{dayEvents.length !== 1 ? 's' : ''}
                                        </div>
                                      </div>
                                      {isTodayEvent && (
                                        <span className="text-xs bg-teal-500 text-white px-3 py-1 rounded-full font-semibold">
                                          Today
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  
                                  {/* Events for this day */}
                                  <div className="p-4 space-y-3">
                                    {(() => {
                                      // Separate events and dress codes, but keep them in order
                                      const sortedDayEvents = [...dayEvents].sort((a, b) => {
                                        // Sort by type: events first, then dress codes
                                        const typeOrder = { event: 0, test: 1, dresscode: 2, holiday: 3 };
                                        return (typeOrder[a.type] || 99) - (typeOrder[b.type] || 99);
                                      });
                                      
                                      return sortedDayEvents.map((event, index) => {
                                        return (
                                          <div
                                            key={`${event.month}-${event.date}-${index}`}
                                            className={`p-4 rounded-lg border-2 ${getEventTypeColor(event.type)} ${
                                              isTodayEvent ? 'ring-1 ring-teal-300' : ''
                                            } ${event.isPast ? 'opacity-75' : ''}`}
                                          >
                                            <div className="flex items-start gap-3">
                                              <span className="text-2xl flex-shrink-0">{getEventTypeIcon(event.type)}</span>
                                              <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 mb-1">
                                                  <h4 className="text-lg font-bold">{event.title}</h4>
                                                </div>
                                                {event.type === 'dresscode' && event.dressCode && (
                                                  <div className="text-base font-bold text-purple-800 mb-2 whitespace-pre-line">
                                                    {event.dressCode}
                                                  </div>
                                                )}
                                                {event.time && (
                                                  <div className="text-sm font-semibold text-gray-700 mb-1">
                                                    ⏰ {event.time}
                                                  </div>
                                                )}
                                                {event.cost && (
                                                  <div className="text-sm font-semibold text-gray-700 mb-1">
                                                    💰 {event.cost}
                                                  </div>
                                                )}
                                                {event.description && event.type !== 'dresscode' && (
                                                  <p className="text-sm opacity-90 mb-2 whitespace-pre-line">{event.description}</p>
                                                )}
                                                {event.image && (
                                                  <div className="mb-3">
                                                    <img 
                                                      src={event.image} 
                                                      alt={event.title}
                                                      className="w-full max-w-md rounded-lg shadow-md border-2 border-gray-200"
                                                      onError={(e) => {
                                                        e.target.style.display = 'none';
                                                      }}
                                                    />
                                                  </div>
                                                )}
                                                {event.url && (
                                                  <a 
                                                    href={event.url} 
                                                    target="_blank" 
                                                    rel="noopener noreferrer"
                                                    className="inline-block mt-2 px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors font-semibold text-sm"
                                                  >
                                                    🔗 Get Tickets / More Info
                                                  </a>
                                                )}
                                    {event.type !== 'dresscode' && (
                                      <div className="text-xs font-semibold opacity-75">
                                        {event.type === 'test' ? '📝 Assessment' : 
                                         event.type === 'event' ? '🎉 School Event' : 
                                         '🏫 Holiday / No School'}
                                      </div>
                                    )}
                                              </div>
                                            </div>
                                          </div>
                                        );
                                      });
                                    })()}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            );
          })()}

          {/* Legend */}
          <div className="bg-teal-50 rounded-2xl p-6">
            <h3 className="text-xl font-bold text-teal-700 mb-4">📋 Event Types</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="flex items-center gap-2">
                <span className="text-lg">📝</span>
                <span className="font-semibold text-red-700">Tests & Assessments</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-lg">🎉</span>
                <span className="font-semibold text-blue-700">School Events & Parties</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-lg">🏫</span>
                <span className="font-semibold text-green-700">Holidays & Breaks</span>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div className="mt-8 bg-gradient-to-r from-teal-500 to-teal-600 rounded-2xl p-6 text-white text-center">
            <h3 className="text-xl font-bold mb-2">🔗 Quick Links</h3>
            <p className="mb-4">For scopes and additional school information:</p>
            <button
              onClick={() => navigateTo('smore')}
              className="inline-block bg-white text-teal-600 px-6 py-3 rounded-full font-bold hover:bg-teal-50 transition-colors transform hover:scale-105 active:scale-95 mr-4"
            >
              📰 Smore Scopes
            </button>
            <button
              onClick={() => navigateTo('newsletter')}
              className="inline-block bg-white text-teal-600 px-6 py-3 rounded-full font-bold hover:bg-teal-50 transition-colors transform hover:scale-105 active:scale-95"
            >
              📱 App Newsletters
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (currentScreen === 'smore') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-200 to-red-300 p-4 md:p-8">
        <div onClick={() => { navigateTo('home'); window.scrollTo({ top: 0, behavior: 'smooth' }); }} 
          className="bg-white text-gray-800 border-2 border-gray-300 px-4 sm:px-6 py-2 sm:py-3 rounded-full shadow-lg inline-flex gap-2 hover:scale-105 hover:bg-gray-50 hover:border-purple-400 cursor-pointer mb-4 font-semibold transition-all">← Back</div>
        
        <div className="max-w-6xl mx-auto bg-white rounded-3xl shadow-2xl p-6 md:p-12">
          <div className="text-center mb-8">
            <div className="text-6xl mb-4">📰</div>
            <h1 className="text-3xl md:text-4xl font-bold text-orange-700 mb-2">Smore Scopes</h1>
            <p className="text-lg text-gray-600">Monthly school scopes and updates for 2025</p>
            <p className="text-sm text-gray-500 mt-2">Updated: January 2025</p>
          </div>

          {/* Current Month Scope */}
          <div className="bg-gradient-to-r from-orange-50 to-red-50 rounded-2xl p-6 mb-8 border-2 border-orange-200">
            <h2 className="text-2xl font-bold text-orange-700 mb-4">📅 Current Month</h2>
            <div className="bg-white rounded-xl p-6 shadow-lg">
              <h3 className="text-xl font-bold text-gray-800 mb-3">Latest Scope</h3>
              <p className="text-gray-600 mb-4">Stay updated with the latest school news, events, and important dates for the 2025-2026 school year.</p>
              <a
                href="https://app.smore.com/n/w5ky1"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-full font-bold transition-colors transform hover:scale-105 active:scale-95"
              >
                📖 Read Latest Scope
              </a>
            </div>
          </div>

          {/* Scope Archive */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-6 mb-8 border-2 border-blue-200">
            <h2 className="text-2xl font-bold text-blue-700 mb-4">📚 Scope Archive</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Latest Scope */}
              <div className="bg-white rounded-xl p-4 shadow-lg hover:shadow-xl transition-shadow border-2 border-orange-300">
                <h3 className="font-bold text-gray-800 mb-2">Latest Scope</h3>
                <p className="text-sm text-gray-600 mb-3">Most recent school news, events, and important dates.</p>
                <a
                  href="https://app.smore.com/n/w5ky1"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 text-sm font-semibold underline"
                >
                  View Scope →
                </a>
              </div>

              {/* September 2025 */}
              <div className="bg-white rounded-xl p-4 shadow-lg hover:shadow-xl transition-shadow">
                <h3 className="font-bold text-gray-800 mb-2">September 2025</h3>
                <p className="text-sm text-gray-600 mb-3">Back to school scope with welcome information and September events.</p>
                <a
                  href="https://app.smore.com/n/a4hu8n"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 text-sm font-semibold underline"
                >
                  View Scope →
                </a>
              </div>

              {/* August 2025 */}
              <div className="bg-white rounded-xl p-4 shadow-lg hover:shadow-xl transition-shadow">
                <h3 className="font-bold text-gray-800 mb-2">August 2025</h3>
                <p className="text-sm text-gray-600 mb-3">Summer wrap-up and preparation for the new school year.</p>
                <a
                  href="https://app.smore.com/n/9tvrj"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 text-sm font-semibold underline"
                >
                  View Scope →
                </a>
              </div>

              {/* July 2025 */}
              <div className="bg-white rounded-xl p-4 shadow-lg hover:shadow-xl transition-shadow">
                <h3 className="font-bold text-gray-800 mb-2">July 2025</h3>
                <p className="text-sm text-gray-600 mb-3">Summer activities and important dates for the upcoming school year.</p>
                <a
                  href="https://app.smore.com/n/9tvrj"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 text-sm font-semibold underline"
                >
                  View Scope →
                </a>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-6 border-2 border-green-200">
            <h2 className="text-2xl font-bold text-green-700 mb-4">🔗 Quick Links</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-white rounded-xl p-4 shadow-lg">
                <h3 className="font-bold text-gray-800 mb-2">📅 School Calendar</h3>
                <p className="text-sm text-gray-600 mb-3">View all school events, holidays, and important dates.</p>
                <button
                  onClick={() => navigateTo('calendar')}
                  className="text-green-600 hover:text-green-800 text-sm font-semibold underline"
                >
                  View Calendar →
                </button>
              </div>
              <div className="bg-white rounded-xl p-4 shadow-lg">
                <h3 className="font-bold text-gray-800 mb-2">📱 App Newsletters</h3>
                <p className="text-sm text-gray-600 mb-3">Access weekly newsletters directly in the app.</p>
                <button
                  onClick={() => navigateTo('newsletter')}
                  className="text-green-600 hover:text-green-800 text-sm font-semibold underline"
                >
                  View App Newsletters →
                </button>
              </div>
            </div>
          </div>

          {/* Direct Smore Link */}
          <div className="mt-8 bg-gradient-to-r from-orange-500 to-red-600 rounded-2xl p-6 text-white text-center">
            <h3 className="text-xl font-bold mb-2">🌐 Visit Smore Website</h3>
            <p className="mb-4">For the most up-to-date scopes and school information, visit the official Smore page:</p>
            <a
              href="https://app.smore.com/n/w5ky1"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-white text-orange-600 px-6 py-3 rounded-full font-bold hover:bg-orange-50 transition-colors transform hover:scale-105 active:scale-95"
            >
              📖 Open Latest Smore Scope
            </a>
          </div>
        </div>
      </div>
    );
  }

  if (currentScreen === 'complete') {
    return (
      <Suspense fallback={<LoadingOverlay message="Loading results..." />}>
        <CompleteScreen
          score={score}
          currentQuestion={currentQuestion}
          currentScreen={currentScreen}
          playSound={playSound}
          resetGameComplete={resetGameComplete}
          showShareModal={showShareModal}
          setShowShareModal={setShowShareModal}
          shareEmail={shareEmail}
          setShareEmail={setShareEmail}
          shareMessage={shareMessage}
          setShareMessage={setShareMessage}
          handleEmailShare={handleEmailShare}
          isSendingEmail={isSendingEmail}
          emailSent={emailSent}
        />
      </Suspense>
    );
  }

  if (currentScreen === 'achievements') {
    return (
      <Suspense fallback={<LoadingOverlay message="Loading achievements..." />}>
        <AchievementsScreen
          progress={progress}
          achievements={achievements}
          themes={themes}
          breadcrumbs={breadcrumbs}
          navigateTo={navigateTo}
        />
      </Suspense>
    );
  }

  if (currentScreen === 'customize') {
    return (
      <Suspense fallback={<LoadingOverlay message="Loading customization..." />}>
        <CustomizeScreen
          progress={progress}
          saveProgress={saveProgress}
          themes={themes}
          avatars={avatars}
          playSound={playSound}
          triggerHaptic={triggerHaptic}
          setShowAudioPreferences={setShowAudioPreferences}
          breadcrumbs={breadcrumbs}
          navigateTo={navigateTo}
        />
      </Suspense>
    );
  }

  if (currentScreen === 'review-mistakes') {
    return (
      <Suspense fallback={<LoadingOverlay message="Loading review..." />}>
        <ReviewMistakesScreen
          progress={progress}
          navigateTo={navigateTo}
          playSound={playSound}
          triggerHaptic={triggerHaptic}
          setCurrentQuestion={setCurrentQuestion}
          setCurrentScreen={setCurrentScreen}
          setSelectedSubject={setSelectedSubject}
          breadcrumbs={breadcrumbs}
        />
      </Suspense>
    );
  }

  if (currentScreen === 'progress') {
    return (
      <Suspense fallback={<LoadingOverlay message="Loading progress..." />}>
        <ProgressScreen
          progress={progress}
          achievements={achievements}
          themes={themes}
          learningStreak={learningStreak}
          confidenceLevels={confidenceLevels}
          breadcrumbs={breadcrumbs}
          navigateTo={navigateTo}
          showShareModal={showShareModal}
          setShowShareModal={setShowShareModal}
          shareEmail={shareEmail}
          setShareEmail={setShareEmail}
          shareMessage={shareMessage}
          setShareMessage={setShareMessage}
          handleProgressEmailShare={handleProgressEmailShare}
          isSendingEmail={isSendingEmail}
          emailSent={emailSent}
        />
      </Suspense>
    );
  }

  if (currentScreen === 'feedback') {
    return (
      <ErrorBoundary name="FeedbackScreen" message="Something went wrong with the feedback screen.">
        <Suspense fallback={<LoadingOverlay message="Loading feedback form..." />}>
          <FeedbackScreen
          progress={progress}
          themes={themes}
          feedbackCategory={feedbackCategory}
          setFeedbackCategory={setFeedbackCategory}
          feedbackMessage={feedbackMessage}
          setFeedbackMessage={setFeedbackMessage}
          feedbackRating={feedbackRating}
          setFeedbackRating={setFeedbackRating}
          submitFeedback={submitFeedback}
          feedback={feedback}
          breadcrumbs={breadcrumbs}
          navigateTo={navigateTo}
        />
        </Suspense>
      </ErrorBoundary>
    );
  }

  if (currentScreen === 'newsletter' || currentScreen.startsWith('newsletter-')) {
    return (
      <ErrorBoundary name="NewsletterScreen" message="Something went wrong loading the newsletter.">
        <Suspense fallback={<LoadingOverlay message="Loading newsletter..." />}>
          <NewsletterScreen
          navigateTo={navigateTo}
        />
        </Suspense>
      </ErrorBoundary>
    );
  }

  if (currentScreen === 'field-trips') {
    return (
      <ErrorBoundary name="FieldTripsScreen" message="Something went wrong loading field trips.">
        <Suspense fallback={<LoadingOverlay message="Loading field trips..." />}>
          <FieldTripsScreen
          onBack={() => navigateTo('home')}
        />
        </Suspense>
      </ErrorBoundary>
    );
  }

  if (currentScreen === 'parent-reference') {
    return (
      <ErrorBoundary name="ParentReferenceScreen" message="Something went wrong loading the parent reference.">
        <Suspense fallback={<LoadingOverlay message="Loading parent reference..." />}>
          <ParentReferenceScreen
          progress={progress}
          themes={themes}
          parentReference={parentReference}
          selectedSubject={selectedSubject}
          selectedCategory={selectedCategory}
          parentQuizMode={parentQuizMode}
          setParentQuizMode={setParentQuizMode}
          parentQuizWord={parentQuizWord}
          setParentQuizWord={setParentQuizWord}
          playSound={playSound}
          triggerHaptic={triggerHaptic}
          breadcrumbs={breadcrumbs}
          navigateTo={navigateTo}
        />
        </Suspense>
      </ErrorBoundary>
    );
  }

  if (currentScreen === 'import') {
    return (
      <ErrorBoundary name="ImportPage" message="Something went wrong loading the import page.">
        <Suspense fallback={<LoadingOverlay message="Loading import page..." />}>
          <ImportPage
            onBack={() => navigateTo('home')}
            onImportComplete={(results) => {
              logger.info('Import completed:', results);
              // Optionally navigate or show success message
            }}
          />
        </Suspense>
      </ErrorBoundary>
    );
  }

  // Debug: Log renderScreen execution
  logger.debug('[App] renderScreen() executing, showProfileManager =', showProfileManager);
  
  return (
    <>
      {/* Offline Indicator */}
      <OfflineIndicator 
        isOnline={isOnline}
        queuedUpdates={queueSize}
      />
      
      <OfflineManager fallbackContent={
        <OfflineFallback 
          title="Learning Adventure Offline"
          message="Some features need internet connection. You can still practice with saved content!"
          icon="🎮"
        />
      }>
        <ErrorBoundary name="GameScreen" message="Something went wrong with the game screen. Don't worry, your progress is saved!">
        <GameScreen
          currentScreen={currentScreen}
          q={q}
          shuffledOptions={shuffledOptions}
          currentQuestion={currentQuestion}
          questionCount={questionCount}
          score={score}
          hasQuestion={hasQuestion}
          answerAnimation={answerAnimation}
          answerLocked={answerLocked}
          hintUsed={hintUsed}
          showHint={showHint}
          showFeedback={showFeedback}
          showExplanation={showExplanation}
          correctAnswer={correctAnswer}
          currentQuestions={currentQuestions}
          selectedPhonicsDifficulty={selectedPhonicsDifficulty}
          setSelectedPhonicsDifficulty={setSelectedPhonicsDifficulty}
          selectedMathDifficulty={selectedMathDifficulty}
          setSelectedMathDifficulty={setSelectedMathDifficulty}
          selectedReadingCategory={selectedReadingCategory}
          setSelectedReadingCategory={setSelectedReadingCategory}
          setCurrentQuestion={setCurrentQuestion}
          handleAnswer={handleAnswer}
          handleHelpClick={handleHelpClick}
          generateHint={generateHint}
          handleFeedbackClose={() => setShowFeedback(null)}
          navigateTo={navigateTo}
          resetGameComplete={resetGameComplete}
          playSound={playSound}
          triggerHaptic={triggerHaptic}
          bgColors={bgColors}
        />
      </ErrorBoundary>
      
      {/* Quick Feedback Button */}
      {currentScreen !== 'home' && currentScreen !== 'feedback' && (
        <button
          onClick={() => navigateTo('feedback')}
          className="fixed bottom-4 right-4 z-40 bg-pink-500 hover:bg-pink-600 text-white p-3 rounded-full shadow-lg hover:scale-110 active:scale-95 transition-transform"
          title="Quick Feedback"
        >
          💬
        </button>
      )}

      {/* Wrong Answer Modal */}

      {/* Legend/Glossary Modal */}
      <LegendModal
        isOpen={showLegendModal}
        onClose={() => setShowLegendModal(false)}
        playSound={playSound}
        triggerHaptic={triggerHaptic}
      />


      {/* Solution Modal - Shows for both correct and incorrect answers */}
      {showWrongAnswerModal && (
        <Suspense fallback={<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000]"><div className="text-white">Loading...</div></div>}>
          <SolutionModal
            isOpen={showWrongAnswerModal}
            solutionData={wrongAnswerData}
        currentQuestion={currentQuestion}
        currentQuestions={currentQuestions}
        score={score}
        questionCount={questionCount}
        currentScreen={currentScreen}
        progress={progress}
        checkAchievements={checkAchievements}
        checkUnlocks={checkUnlocks}
        saveProgress={saveProgress}
        onClose={() => {
          setShowWrongAnswerModal(false);
          setWrongAnswerData(null);
          setShowFeedback(null);
          setAnswerAnimation('');
          setCorrectAnswer('');
          setAnswerLocked(false);
          setHintUsed(false);
          setShowHint(false);
        }}
        setCurrentQuestion={setCurrentQuestion}
        setCurrentScreen={(screen) => navigateTo(screen)}
        playSound={playSound}
        triggerHaptic={triggerHaptic}
          />
        </Suspense>
      )}
      

      {/* Audio Preferences Modal */}
      {showAudioPreferences && (
        <Suspense fallback={<div />}>
          <AudioPreferences 
            isOpen={showAudioPreferences}
            onClose={() => setShowAudioPreferences(false)}
          />
        </Suspense>
      )}
      
      {/* Loading overlay disabled to prevent stuck modal during gameplay */}

      {/* Performance Dashboard */}
      {showPerformanceDashboard && (
        <Suspense fallback={null}>
          <PerformanceDashboard 
            isVisible={showPerformanceDashboard}
            onClose={() => setShowPerformanceDashboard(false)}
          />
        </Suspense>
      )}
    </OfflineManager>
    </>
  );
  } // End renderScreen
  
  // Render LazyProfileManager OUTSIDE renderScreen to ensure it always renders
  const profileManagerElement = showProfileManager ? (
    (() => {
      logger.info('[App] RENDERING LazyProfileManager - showProfileManager is true');
      
      try {
        return (
          <ErrorBoundary name="ProfileManager" message="Something went wrong with the profile manager.">
            <LazyProfileManager 
              onClose={() => {
                logger.info('[App] ProfileManager onClose called');
                setShowProfileManager(false);
              }}
              progress={progress}
              saveProgress={saveProgress}
              avatars={avatars}
            />
          </ErrorBoundary>
        );
      } catch (error) {
        logger.error('[App] ERROR rendering LazyProfileManager:', error);
        return <div style={{position: 'fixed', top: 0, left: 0, zIndex: 99999, background: 'red', color: 'white', padding: '20px'}}>Error loading profile manager: {error.message}</div>;
      }
    })()
  ) : null;
  
  return (
    <>
      {/* Help System - Render outside renderScreen to ensure it's always on top */}
      {showHelp && (
        <Suspense fallback={<div />}>
          <HelpSystem 
            isOpen={showHelp} 
            onClose={() => {
              logger.debug('[App] HelpSystem onClose called, setting showHelp to false');
              setShowHelp(false);
            }} 
          />
        </Suspense>
      )}
      {/* Chat Support - Always available */}
      <Suspense fallback={<div />}>
        <ChatSupport />
      </Suspense>
      {profileManagerElement}
      {renderScreen()}
    </>
  );
}

// Main App Component with URL Routing
const App = () => {
  const [showSplashScreen, setShowSplashScreen] = useState(true);
  const [showNotificationSettings, setShowNotificationSettings] = useState(false);
  const [pwaRegistration, setPwaRegistration] = useState(null);

  useNormalizeGamePath();
  useErrorHandlingInit(setPwaRegistration);

  const handleSplashComplete = () => {
    setShowSplashScreen(false);
  };

  // Theme is applied automatically by useTheme hook in EmmyStudyGame

  return (
    <ErrorBoundary>
      <UserProvider>
        <AccessibilityProvider>
          <AccessibleApp>
            <HashRouter>
              <Routes>
                <Route path="/auth/callback" element={<AuthCallback />} />
                <Route path="/invite/:token" element={<InvitationPage />} />
                <Route path="/parent-dashboard" element={<ParentDashboardWrapper />} />
                <Route path="*" element={
                  <>
                    <SkipToContent targetId="main-content" />
                    <div className="enhanced-focus keyboard-navigation">
                      <EmmyStudyGame />
                    </div>
                  </>
                } />
              </Routes>
            </HashRouter>
          </AccessibleApp>
        </AccessibilityProvider>
      </UserProvider>
    </ErrorBoundary>
  );
};

export default App;

